// src/pages/BuildingDesignerPage.jsx
//
// Stage 2. The page owns the whole state machine:
//   - what type is selected
//   - what's in-progress (drawing) and its openings
//   - which click mode
//   - which opening we're currently placing (if any)
// The canvas is dumb (just calls onCanvasClick(x,y)); the page
// interprets each click based on the current mode.

import React, { useState, useMemo } from "react";
import styled from "styled-components";
import ElementForm, {
  makeEmptyDrawing,
  shaftDerived,
  stairsDerived,
  rescaleOpenings,
  rescaleCustomOpenings,
} from "../components/BuildingDesigner/ElementForm";
import ElementsList from "../components/BuildingDesigner/ElementsList";
import PlanCanvas from "../components/BuildingDesigner/PlanCanvas";
import TypePicker from "../components/BuildingDesigner/TypePicker";
import {
  saveFloorAndDownload,
  openProjectJson,
} from "../components/BuildingDesigner/utils";
import {
  detectRectWall,
  distAlongRectWall,
  snapCustomPoint,
  polygonCentroid,
  detectCustomEdge,
  polygonIsClockwise,
  edgeSwing,
} from "../components/BuildingDesigner/geoUtils";

import SEO from "../components/SEO";

const PageContainer = styled.div`
  /* Anchor to the viewport below the fixed navbar (100 px high) so we
     don't depend on the parent LayoutContainer having a defined height.
     Prevents the middle-panel SVG from collapsing to a tiny width and
     then growing to hundreds of thousands of pixels tall. */
  position: fixed;
  top: 100px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: row;
  gap: 8px;
  padding: 8px;
  box-sizing: border-box;
  background: #f8f8f8;
  overflow: hidden;
`;
const LeftPanel = styled.div`
  flex: 0 0 340px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
  padding: 12px;
  height: 100%;
  box-sizing: border-box;
`;
const MiddlePanel = styled.div`
  flex: 1 1 auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 300px;
`;
const RightPanel = styled.div`
  flex: 0 0 220px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
  padding: 12px;
  height: 100%;
  box-sizing: border-box;
`;
const SaveButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: teal;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 12px;
  &:hover {
    background: #007070;
  }
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;
const SubBtn = styled.button`
  padding: 6px 10px;
  font-size: 12px;
  background: white;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
`;

export default function BuildingDesignerPage() {
  const [floor, setFloor] = useState({
    name: "MyBuilding",
    rooms: [],
    stairs: [],
    shafts: [],
    customs: [],
    beams: [],
    columns: [],
    slabs: [],
    texts: [],
  });
  const [bbox, setBbox] = useState({
    width: 10,
    height: 12,
    offsetX: 0,
    offsetY: 0,
  });

  const [selectedType, setSelectedType] = useState(null);

  // The in-progress element and its openings.
  const [drawing, setDrawing] = useState(null);

  // Rect placement mode.
  const [clickMode, setClickMode] = useState("corner");
  const [firstCorner, setFirstCorner] = useState(null); // for twoCorner mode

  // Opening placement.
  //   openingMode:  null | "door" | "window" | "open_door"
  //   openingDraft: shape depends on kind
  //     door/window:  { kind, wall, pos, width, rotation, hinge_side, swing }
  //     open_door:    { kind, wall, pos1, pos2 }
  const [openingMode, setOpeningMode] = useState(null);
  const [openingDraft, setOpeningDraft] = useState(null);

  // When editing an already-committed room, this holds its index into
  // floor.rooms so "Save changes" replaces the same slot. null = adding
  // a fresh room.
  const [editIndex, setEditIndex] = useState(null);
  // "room" or "custom" — tells the commit handler which array to update.
  const [editKind, setEditKind] = useState(null);

  const [saving, setSaving] = useState(false);

  // ─── Undo / Redo (Stage 3s) ────────────────────────────────────────
  // Each stack holds up to MAX_UNDO snapshots of the floor object taken
  // BEFORE a mutating action. Push on every commit / delete; pop on
  // Ctrl+Z. Any NEW mutating action clears the redo stack (standard
  // behaviour). Undo is DISABLED while the user is editing an element
  // (form open) to avoid half-typed values disappearing.
  const MAX_UNDO = 20;
  const [undoStack, setUndoStack] = useState([]); // array of floor snapshots
  const [redoStack, setRedoStack] = useState([]);

  /**
   * Wrap every mutation to `floor` so the previous state is captured
   * for undo. Call this instead of setFloor(...) for user actions that
   * should be undoable (add / delete / save-edit). setFloor still works
   * for non-undoable state changes (like "New project" or "Load JSON")
   * which reset the stacks separately below.
   */
  // Stage 4d: enforce unique wall names on every commit.
  // Any wall that has a duplicate name (or no name at all) gets
  // renamed beam_N using the smallest available N. Real user-typed
  // names that are already unique are kept as-is.
  const renumberDuplicateWalls = (f) => {
    const walls = f.beams || [];
    if (walls.length === 0) return f;
    // Which names are ALREADY used (first occurrence wins)
    const seen = new Set();
    let nextIdx = 1;
    const pickFreeIdx = () => {
      while (seen.has(`beam_${nextIdx}`)) nextIdx += 1;
      const name = `beam_${nextIdx}`;
      nextIdx += 1;
      return name;
    };
    const renamed = walls.map((w) => {
      const nm = (w.name || "").trim();
      if (nm && !seen.has(nm)) {
        seen.add(nm);
        return w;
      }
      // duplicate or empty — assign a fresh beam_N
      const newName = pickFreeIdx();
      seen.add(newName);
      return { ...w, name: newName };
    });
    // Fast reference-equality check: if nothing changed, return original
    const unchanged = renamed.every((w, i) => w === walls[i]);
    if (unchanged) return f;
    return { ...f, beams: renamed };
  };

  const commitFloor = (nextFloorOrFn) => {
    setUndoStack((prev) => {
      const snap = floor; // capture current (pre-change) state
      const next = [...prev, snap];
      // Cap depth
      if (next.length > MAX_UNDO) next.shift();
      return next;
    });
    setRedoStack([]); // any new action clears redo
    if (typeof nextFloorOrFn === "function") {
      setFloor((prev) => renumberDuplicateWalls(nextFloorOrFn(prev)));
    } else {
      setFloor(renumberDuplicateWalls(nextFloorOrFn));
    }
  };

  const canUndo = undoStack.length > 0 && editIndex === null;
  const canRedo = redoStack.length > 0 && editIndex === null;

  // ─── Shift X / Y (Stage 3w) ────────────────────────────────────────
  // Mirrors the CLI Python program's "Shift the whole drawing by X,Y?"
  // prompt. Moves rooms/customs/shafts/stairs/walls by (dx, dy). Opening
  // positions are LOCAL to their wall so they don't shift. Elevator
  // positions are LOCAL to their shaft so they don't shift either.
  const [shiftPanelOpen, setShiftPanelOpen] = useState(false);
  const [shiftXInput, setShiftXInput] = useState("");
  const [shiftYInput, setShiftYInput] = useState("");

  const shiftFloorBy = (dx, dy) => {
    if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return; // no-op
    const next = {
      ...floor,
      rooms: (floor.rooms || []).map((r) => ({
        ...r,
        x: r.x + dx,
        y: r.y + dy,
      })),
      customs: (floor.customs || []).map((c) => ({
        ...c,
        points: (c.points || []).map(([px, py]) => [px + dx, py + dy]),
      })),
      shafts: (floor.shafts || []).map((s) => ({
        ...s,
        x: s.x + dx,
        y: s.y + dy,
      })),
      stairs: (floor.stairs || []).map((s) => ({
        ...s,
        x: s.x + dx,
        y: s.y + dy,
      })),
      beams: (floor.beams || []).map((w) => ({
        ...w,
        x1: w.x1 + dx,
        y1: w.y1 + dy,
        x2: w.x2 + dx,
        y2: w.y2 + dy,
      })),
      // Stage 3x: text labels have absolute positions too, so they shift.
      texts: (floor.texts || []).map((t) => ({
        ...t,
        x: t.x + dx,
        y: t.y + dy,
      })),
    };
    commitFloor(next);
  };

  // Auto-shift: compute min X/Y across every element and shift by the
  // negation so the drawing starts at (0, 0). Handles negative coords
  // and any element type.
  const shiftToOrigin = () => {
    let minX = Infinity,
      minY = Infinity;
    (floor.rooms || []).forEach((r) => {
      minX = Math.min(minX, r.x);
      minY = Math.min(minY, r.y);
    });
    (floor.customs || []).forEach((c) => {
      (c.points || []).forEach(([px, py]) => {
        minX = Math.min(minX, px);
        minY = Math.min(minY, py);
      });
    });
    (floor.shafts || []).forEach((s) => {
      minX = Math.min(minX, s.x);
      minY = Math.min(minY, s.y);
    });
    (floor.stairs || []).forEach((s) => {
      minX = Math.min(minX, s.x);
      minY = Math.min(minY, s.y);
    });
    (floor.beams || []).forEach((w) => {
      minX = Math.min(minX, w.x1, w.x2);
      minY = Math.min(minY, w.y1, w.y2);
    });
    (floor.texts || []).forEach((t) => {
      minX = Math.min(minX, t.x);
      minY = Math.min(minY, t.y);
    });
    if (!isFinite(minX) || !isFinite(minY)) return; // empty floor
    shiftFloorBy(-minX, -minY);
  };

  const canShift =
    editIndex === null && !selectedType && !openingMode && !openingDraft;

  const applyShiftFromInputs = () => {
    const dx = parseFloat(shiftXInput);
    const dy = parseFloat(shiftYInput);
    if (isNaN(dx) && isNaN(dy)) return;
    shiftFloorBy(isNaN(dx) ? 0 : dx, isNaN(dy) ? 0 : dy);
    setShiftPanelOpen(false);
    setShiftXInput("");
    setShiftYInput("");
  };

  const doUndo = () => {
    if (!canUndo) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack(undoStack.slice(0, -1));
    setRedoStack((r) => {
      const next = [...r, floor];
      if (next.length > MAX_UNDO) next.shift();
      return next;
    });
    setFloor(prev);
    // Also clear any in-progress draft so a stale drawing doesn't
    // reference something that was just restored/removed.
    setDrawing(null);
    setSelectedType(null);
    setFirstCorner(null);
    setOpeningMode(null);
    setOpeningDraft(null);
  };

  const doRedo = () => {
    if (!canRedo) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack(redoStack.slice(0, -1));
    setUndoStack((u) => {
      const nx = [...u, floor];
      if (nx.length > MAX_UNDO) nx.shift();
      return nx;
    });
    setFloor(next);
    setDrawing(null);
    setSelectedType(null);
    setFirstCorner(null);
    setOpeningMode(null);
    setOpeningDraft(null);
  };

  // Global Ctrl+Z / Ctrl+Y (also Ctrl+Shift+Z for redo).
  React.useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const meta = e.ctrlKey || e.metaKey; // Cmd on Mac
      if (!meta) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        doUndo();
      } else if (key === "y" || (key === "z" && e.shiftKey)) {
        e.preventDefault();
        doRedo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoStack, redoStack, floor, editIndex]);

  // ─── Multi-select (Stage 3u ship 1) ───────────────────────────────
  // Selection is a plain array of { kind, index } pairs. Multiple kinds
  // can co-exist (walls + rooms + shafts) in the same selection.
  //
  //   kind ∈ { "room", "beam", "custom", "shaft", "stairs" }
  //   index = position inside the corresponding floor.<...> array
  //
  // Ctrl+click on a canvas element toggles its membership. Opening an
  // edit form clears the multi-selection (per user Q3). Delete key
  // deletes every selected element in ONE undo action (per Q2u).
  const [selection, setSelection] = useState([]); // array of {kind,index}

  // Stage 6f: live preview for a pending multi-selection group change.
  //   {type:'move', dx, dy}         — whole group translated
  //   {type:'resize', edge, delta}  — every selected ROOM's width/height
  //                                    shifted by the same delta
  //   null                          — nothing pending
  // Rendering-only until the user clicks "Save changes" (or it's
  // discarded via "Cancel" / ESC) — `floor`/undo stay untouched til then.
  const [groupPendingChange, setGroupPendingChange] = useState(null);

  // Stage 4e2: when DETECT_ROOM auto-selected walls for a specific
  // room bbox, remember that bbox so the Convert-to-room bar can use it
  // directly without recomputing (which would fail when selected walls
  // extend past the room boundary).
  const [detectedRoomBbox, setDetectedRoomBbox] = useState(null);

  // Stage 5a: mirror-selection panel open/close state.
  const [mirrorPanelOpen, setMirrorPanelOpen] = useState(false);
  // Stage 5b: mirror axis + line, lifted to page level so a canvas click
  // can set the line position (same feel as picking CUSTOM polygon
  // points). Default line = 0, matches "m=0 as default value" spec.
  const [mirrorAxis, setMirrorAxis] = useState("vertical");
  const [mirrorLine, setMirrorLine] = useState(0);

  // Stage 5d: copy/paste. Clipboard is independent of `floor` state, so
  // it naturally survives New Project / Open Project — copy in one
  // project, switch files, paste into the other ("new canvas").
  //   clipboard = { items: [{kind, data}], refX, refY } | null
  //   refX/refY = SW corner of the copied selection's combined bbox —
  //   the reference point the paste-click repositions.
  const [clipboard, setClipboard] = useState(null);
  const [pastePanelOpen, setPastePanelOpen] = useState(false);
  const [pasteX, setPasteX] = useState(0);
  const [pasteY, setPasteY] = useState(0);

  // Stage 4m: user-chosen input precision for coordinate fields
  // (x, y, width, height, wall endpoints, opening positions, etc.).
  // 0.1 = coarse (default), 0.01 = fine.
  const [inputStep, setInputStep] = useState(0.1);

  // Stage 3x.5: whether the "text labels" overlay renders on canvas.
  const [showTexts, setShowTexts] = useState(true);

  const isSelected = (kind, index) =>
    selection.some((s) => s.kind === kind && s.index === index);

  const toggleSelection = (kind, index) => {
    setDetectedRoomBbox(null); // manual change invalidates the detected room
    setSelection((prev) => {
      const i = prev.findIndex((s) => s.kind === kind && s.index === index);
      if (i >= 0) {
        const next = [...prev];
        next.splice(i, 1);
        return next;
      }
      return [...prev, { kind, index }];
    });
  };

  const clearSelection = () => {
    setDetectedRoomBbox(null);
    setSelection([]);
  };

  // Delete every selected element in ONE undo entry. We build the new
  // floor object by filtering each array in one pass, so the operation
  // is a single commitFloor() call — Ctrl+Z restores them all.
  const deleteSelected = () => {
    if (selection.length === 0) return;
    // Group indices to remove by kind
    const removeByKind = {};
    for (const { kind, index } of selection) {
      (removeByKind[kind] = removeByKind[kind] || new Set()).add(index);
    }
    const arrOf = {
      room: floor.rooms || [],
      beam: floor.beams || [],
      column: floor.columns || [],
      slab: floor.slabs || [],
      custom: floor.customs || [],
      shaft: floor.shafts || [],
      stairs: floor.stairs || [],
      text: floor.texts || [],
    };
    const filtered = {};
    for (const [k, arr] of Object.entries(arrOf)) {
      const rm = removeByKind[k];
      filtered[k] = rm ? arr.filter((_, i) => !rm.has(i)) : arr;
    }
    commitFloor({
      ...floor,
      rooms: filtered.room,
      beams: filtered.beam,
      columns: filtered.column,
      slabs: filtered.slab,
      customs: filtered.custom,
      shafts: filtered.shaft,
      stairs: filtered.stairs,
      texts: filtered.text,
    });
    setSelection([]);
    // If the currently-edited element is inside the deletion, close edit.
    if (
      editIndex !== null &&
      editKind &&
      removeByKind[editKind]?.has(editIndex)
    ) {
      setEditIndex(null);
      setEditKind(null);
      resetToolState();
    }
  };

  // ── Merge selected collinear beams into one (manual action) ─────────
  // Takes 2+ selected beams that are collinear within AXIS_TOL (5 cm) —
  // whether they overlap, touch, or have gaps between them — and replaces
  // them with ONE beam spanning from the minimum to the maximum
  // coordinate along their shared axis (gaps are filled). The result is
  // snapped to the LOWEST-INDEX selected beam's axis so it comes out
  // perfectly straight, and keeps that beam's name and label. One undo.
  const MERGE_AXIS_TOL = 0.05;

  const selectedBeamIndicesSorted = () =>
    selection
      .filter((s) => s.kind === "beam")
      .map((s) => s.index)
      .sort((a, b) => a - b);

  // Can the current selection be merged? (2+ beams, all collinear.)
  const canMergeSelectedBeams = () => {
    const beams = floor.beams || [];
    const idxs = selectedBeamIndicesSorted();
    // must be all-beam selection of 2+ (no other kinds mixed in)
    if (idxs.length < 2) return false;
    if (idxs.length !== selection.length) return false; // mixed kinds
    const items = idxs.map((i) => beams[i]).filter(Boolean);
    if (items.length !== idxs.length) return false;

    const isH = (b) => Math.abs(b.y1 - b.y2) < 1e-6;
    const isV = (b) => Math.abs(b.x1 - b.x2) < 1e-6;

    if (items.every(isH)) {
      // all horizontal → all y within tol of the first
      const y0 = items[0].y1;
      return items.every((b) => Math.abs(b.y1 - y0) <= MERGE_AXIS_TOL);
    }
    if (items.every(isV)) {
      const x0 = items[0].x1;
      return items.every((b) => Math.abs(b.x1 - x0) <= MERGE_AXIS_TOL);
    }
    return false; // mixed orientations or a diagonal → not collinear
  };

  const mergeSelectedBeams = () => {
    const beams = floor.beams || [];
    const idxs = selectedBeamIndicesSorted();
    if (idxs.length < 2) return;

    const items = idxs.map((i) => beams[i]);
    const isH = (b) => Math.abs(b.y1 - b.y2) < 1e-6;
    const isV = (b) => Math.abs(b.x1 - b.x2) < 1e-6;

    const allH = items.every(isH);
    const allV = items.every(isV);
    if (!allH && !allV) {
      window.alert("Selected beams are not collinear — can't merge.");
      return;
    }
    // collinearity within tolerance
    if (allH) {
      const y0 = items[0].y1;
      if (!items.every((b) => Math.abs(b.y1 - y0) <= MERGE_AXIS_TOL)) {
        window.alert("Selected beams are not collinear — can't merge.");
        return;
      }
    } else {
      const x0 = items[0].x1;
      if (!items.every((b) => Math.abs(b.x1 - x0) <= MERGE_AXIS_TOL)) {
        window.alert("Selected beams are not collinear — can't merge.");
        return;
      }
    }

    const first = items[0]; // lowest-index selected beam
    let merged;
    if (allH) {
      const xs = items.flatMap((b) => [b.x1, b.x2]);
      const y = first.y1; // snap to first beam's axis
      merged = {
        name: first.name,
        label: first.label || "",
        x1: Math.min(...xs),
        y1: y,
        x2: Math.max(...xs),
        y2: y,
      };
    } else {
      const ys = items.flatMap((b) => [b.y1, b.y2]);
      const x = first.x1;
      merged = {
        name: first.name,
        label: first.label || "",
        x1: x,
        y1: Math.min(...ys),
        x2: x,
        y2: Math.max(...ys),
      };
    }

    const removeSet = new Set(idxs);
    const nextBeams = beams.filter((_, i) => !removeSet.has(i)).concat(merged);
    commitFloor({ ...floor, beams: nextBeams });
    setSelection([]);
  };

  // ── Define slab panel from selected bounding beams ───────────────────
  // Ctrl-select the 4 beams that surround a panel (2 horizontal + 2
  // vertical), then click "Define Panel". The enclosing rectangle is
  // computed from the beams' positions (they need NOT meet endpoint-to-
  // endpoint — verticals may cross the horizontals mid-span). Then:
  //   solid = area < 36 m²   ·   flat = area ≥ 36 m²
  // (A valid 2H+2V selection is always rectangular, so the only reason to
  //  become flat is the 36 m² limit. An invalid selection creates nothing.)
  const PANEL_TOL = 0.05;
  const SOLID_MAX_AREA = 36.0;

  // From the selected beams, if they are exactly 2 horizontal + 2 vertical,
  // return the bounding rectangle corners [ [x,y]... ] and its area; else null.
  const panelRectFromBeams = (beamList) => {
    const isH = (b) => Math.abs(b.y1 - b.y2) < PANEL_TOL;
    const isV = (b) => Math.abs(b.x1 - b.x2) < PANEL_TOL;
    const hs = beamList.filter(isH);
    const vs = beamList.filter(isV);
    if (hs.length !== 2 || vs.length !== 2) return null;

    // the two horizontals give bottom/top y; the two verticals give left/right x
    const ys = hs.map((b) => (b.y1 + b.y2) / 2);
    const xs = vs.map((b) => (b.x1 + b.x2) / 2);
    const y0 = Math.min(...ys),
      y1 = Math.max(...ys);
    const x0 = Math.min(...xs),
      x1 = Math.max(...xs);
    if (x1 - x0 < PANEL_TOL || y1 - y0 < PANEL_TOL) return null;

    const corners = [
      [x0, y0],
      [x1, y0],
      [x1, y1],
      [x0, y1],
    ];
    const area = (x1 - x0) * (y1 - y0);
    return { corners, area };
  };

  const canDefinePanel = () => {
    if (selection.length !== 4) return false;
    if (!selection.every((s) => s.kind === "beam")) return false;
    const beams = floor.beams || [];
    const chosen = selection.map((s) => beams[s.index]).filter(Boolean);
    return panelRectFromBeams(chosen) !== null;
  };

  const definePanel = () => {
    const beams = floor.beams || [];
    const chosen = selection.map((s) => beams[s.index]).filter(Boolean);
    const rect = panelRectFromBeams(chosen);
    if (!rect) {
      window.alert(
        "Select exactly 4 beams that bound a rectangle " +
          "(2 horizontal + 2 vertical)."
      );
      return;
    }
    const kind = rect.area < SOLID_MAX_AREA ? "solid" : "flat";

    const nSolid = (floor.slabs || []).filter((s) => s.kind === "solid").length;
    const nFlat = (floor.slabs || []).filter((s) => s.kind === "flat").length;
    const name = kind === "solid" ? `S${nSolid + 1}` : `F${nFlat + 1}`;

    const slab = {
      name,
      label: name,
      kind,
      points: rect.corners.map(([x, y]) => [x, y]),
      ts: 120,
      gamma_c: 25,
      FC: 1.5,
      LL: 2.0,
    };
    commitFloor({ ...floor, slabs: [...(floor.slabs || []), slab] });
    setSelection([]);
    window.alert(
      `Defined ${kind.toUpperCase()} slab "${name}"  (area ${rect.area.toFixed(2)} m²).`
    );
  };

  // ── Set carried beam (T-junction framing) ───────────────────────────
  // With exactly 2 perpendicular beams selected (first-clicked = CARRIER,
  // second = CARRIED), pull the carried beam's meeting end back by
  // HALF_BREADTH so it stops at the carrier's FACE instead of crossing to
  // its centerline. This is the standard way to show a carried beam
  // framing into a carrier: e.g. carrier horizontal at y=20, carried
  // vertical coming up from below → carried top y becomes 19.9 (the
  // carrier's bottom face). Leaves a deliberate 0.1 m gap. One undo.
  const HALF_BREADTH = 0.1;

  // Info about the current 2-beam selection for the button: returns
  // { ok, carriedName } — ok=true only when exactly 2 perpendicular beams
  // are selected (in click order). carriedName is selection[1]'s name.
  const carriedInfo = () => {
    const beams = floor.beams || [];
    if (selection.length !== 2) return { ok: false };
    if (!selection.every((s) => s.kind === "beam")) return { ok: false };
    const a = beams[selection[0].index];
    const b = beams[selection[1].index];
    if (!a || !b) return { ok: false };
    const isH = (bm) => Math.abs(bm.y1 - bm.y2) < 1e-6;
    const isV = (bm) => Math.abs(bm.x1 - bm.x2) < 1e-6;
    const perp = (isH(a) && isV(b)) || (isV(a) && isH(b));
    if (!perp) return { ok: false };
    return {
      ok: true,
      carriedName: b.name || `beam_${selection[1].index + 1}`,
    };
  };

  const setCarriedBeam = () => {
    const beams = floor.beams || [];
    if (selection.length !== 2 || !selection.every((s) => s.kind === "beam")) {
      window.alert(
        "Select exactly two beams one at a time (first = carrier, " +
          "second = carried)."
      );
      return;
    }
    const carrierIdx = selection[0].index;
    const carriedIdx = selection[1].index;
    const carrier = beams[carrierIdx];
    const carried = beams[carriedIdx];
    if (!carrier || !carried) return;

    const isH = (bm) => Math.abs(bm.y1 - bm.y2) < 1e-6;
    const isV = (bm) => Math.abs(bm.x1 - bm.x2) < 1e-6;

    if (!((isH(carrier) && isV(carried)) || (isV(carrier) && isH(carried)))) {
      window.alert(
        "The two beams must be perpendicular (one horizontal, one vertical)."
      );
      return;
    }

    // L-corner tolerance: if the carrier's nearer END sits within this
    // distance of the carried beam's line, the junction is an L-corner and
    // the carrier is extended to the carried's FAR face. Larger than a door
    // gap / human error so short carriers still close the corner. If the
    // carried instead meets the carrier's INTERIOR (carrier end far away),
    // it's a plain T-junction — carried retreats only, carrier untouched.
    const L_TOL = 2.0;

    let updatedCarried = { ...carried };
    let updatedCarrier = { ...carrier };
    let carrierChanged = false;

    if (isH(carrier) && isV(carried)) {
      // Carrier horizontal at y = carrierY, spanning x. Carried vertical
      // at x = carriedX, spanning y.
      const carrierY = carrier.y1;
      const carriedX = carried.x1;

      // --- carried: retreat its meeting end to the carrier's near face ---
      const d1 = Math.abs(carried.y1 - carrierY);
      const d2 = Math.abs(carried.y2 - carrierY);
      const meetIsP1 = d1 <= d2;
      const farY = meetIsP1 ? carried.y2 : carried.y1;
      const cSign = farY < carrierY ? -1 : +1; // toward carried's body
      const newMeetY = carrierY + cSign * HALF_BREADTH;
      if (meetIsP1) updatedCarried.y1 = newMeetY;
      else updatedCarried.y2 = newMeetY;

      // --- carrier: is its nearer END at this corner (L)? ---
      // carrier ends are (x1,y) and (x2,y); corner is at x = carriedX.
      const e1 = Math.abs(carrier.x1 - carriedX);
      const e2 = Math.abs(carrier.x2 - carriedX);
      const cornerIsX1 = e1 <= e2;
      const nearEnd = cornerIsX1 ? carrier.x1 : carrier.x2;
      if (Math.abs(nearEnd - carriedX) <= L_TOL) {
        // extend carrier to the carried's FAR face: 0.1 past carriedX,
        // in the direction away from the carrier's body.
        const carrierFarX = cornerIsX1 ? carrier.x2 : carrier.x1;
        const eSign = carrierFarX < carriedX ? +1 : -1; // outward past corner
        const newEndX = carriedX + eSign * HALF_BREADTH;
        if (cornerIsX1) updatedCarrier.x1 = newEndX;
        else updatedCarrier.x2 = newEndX;
        carrierChanged = true;
      }
    } else {
      // Carrier vertical at x = carrierX, spanning y. Carried horizontal
      // at y = carriedY, spanning x.
      const carrierX = carrier.x1;
      const carriedY = carried.y1;

      // --- carried: retreat its meeting end to the carrier's near face ---
      const d1 = Math.abs(carried.x1 - carrierX);
      const d2 = Math.abs(carried.x2 - carrierX);
      const meetIsP1 = d1 <= d2;
      const farX = meetIsP1 ? carried.x2 : carried.x1;
      const cSign = farX < carrierX ? -1 : +1;
      const newMeetX = carrierX + cSign * HALF_BREADTH;
      if (meetIsP1) updatedCarried.x1 = newMeetX;
      else updatedCarried.x2 = newMeetX;

      // --- carrier: is its nearer END at this corner (L)? ---
      const e1 = Math.abs(carrier.y1 - carriedY);
      const e2 = Math.abs(carrier.y2 - carriedY);
      const cornerIsY1 = e1 <= e2;
      const nearEnd = cornerIsY1 ? carrier.y1 : carrier.y2;
      if (Math.abs(nearEnd - carriedY) <= L_TOL) {
        const carrierFarY = cornerIsY1 ? carrier.y2 : carrier.y1;
        const eSign = carrierFarY < carriedY ? +1 : -1;
        const newEndY = carriedY + eSign * HALF_BREADTH;
        if (cornerIsY1) updatedCarrier.y1 = newEndY;
        else updatedCarrier.y2 = newEndY;
        carrierChanged = true;
      }
    }

    const nextBeams = beams.map((bm, i) => {
      if (i === carriedIdx) return updatedCarried;
      if (i === carrierIdx && carrierChanged) return updatedCarrier;
      return bm;
    });
    commitFloor({ ...floor, beams: nextBeams });
    setSelection([]);
  };

  // ── Convert selected walls into a room element (Stage 4c) ─────────
  // v1: rectangle-only. Takes all walls in the current selection, checks
  // that they form an axis-aligned closed rectangle, removes them, and
  // adds a new room/kitchen/bathroom/etc. element with the derived
  // bounding box. Single Ctrl+Z reverses everything.
  //
  //   selectedWallsFormRect() — validation used to enable/disable buttons
  //   convertSelectionToRoom(roomType) — actually performs the conversion
  const selectedWallIndices = () =>
    selection.filter((s) => s.kind === "beam").map((s) => s.index);

  // ── Auto-detect room from a click point (Stage 4e) ─────────────────
  // Given a point (px, py) inside a room, find the enclosing rectangle
  // by walking outward in the 4 cardinal directions until we hit a wall.
  // Returns { minX, minY, maxX, maxY, wallIndices } on success, else null.
  //
  //   * only considers axis-aligned walls (H or V)
  //   * a wall "blocks" the ray only if it CROSSES the ray line
  //     (its perpendicular extent covers the ray's fixed coordinate)
  //   * side-coverage is NOT re-verified here — the yellow-bar's
  //     selectedWallsFormRect() handles that when the walls are put
  //     into the selection.
  const detectRoomAt = (px, py) => {
    const walls = floor.beams || [];
    const EPS = 0.01;
    let minX = -Infinity,
      maxX = Infinity;
    let minY = -Infinity,
      maxY = Infinity;

    // Ray RIGHT along y=py: find nearest vertical wall with x > px
    // whose y-extent covers py.
    for (const w of walls) {
      if (Math.abs(w.x1 - w.x2) < EPS) {
        // vertical
        const x = (w.x1 + w.x2) / 2;
        const yMin = Math.min(w.y1, w.y2);
        const yMax = Math.max(w.y1, w.y2);
        if (py > yMin - EPS && py < yMax + EPS) {
          if (x > px + EPS && x < maxX) maxX = x;
          if (x < px - EPS && x > minX) minX = x;
        }
      } else if (Math.abs(w.y1 - w.y2) < EPS) {
        // horizontal
        const y = (w.y1 + w.y2) / 2;
        const xMin = Math.min(w.x1, w.x2);
        const xMax = Math.max(w.x1, w.x2);
        if (px > xMin - EPS && px < xMax + EPS) {
          if (y > py + EPS && y < maxY) maxY = y;
          if (y < py - EPS && y > minY) minY = y;
        }
      }
    }
    if (
      !isFinite(minX) ||
      !isFinite(maxX) ||
      !isFinite(minY) ||
      !isFinite(maxY)
    )
      return null;

    // Now collect every wall that lies ON one of the 4 sides of this bbox.
    const wallIndices = [];
    walls.forEach((w, i) => {
      const isH = Math.abs(w.y1 - w.y2) < EPS;
      const isV = Math.abs(w.x1 - w.x2) < EPS;
      if (isH) {
        const y = (w.y1 + w.y2) / 2;
        const wMin = Math.min(w.x1, w.x2);
        const wMax = Math.max(w.x1, w.x2);
        // Must be on top or bottom side AND overlap the bbox x-range.
        if (
          (Math.abs(y - minY) < EPS || Math.abs(y - maxY) < EPS) &&
          wMax >= minX - EPS &&
          wMin <= maxX + EPS
        ) {
          wallIndices.push(i);
        }
      } else if (isV) {
        const x = (w.x1 + w.x2) / 2;
        const wMin = Math.min(w.y1, w.y2);
        const wMax = Math.max(w.y1, w.y2);
        if (
          (Math.abs(x - minX) < EPS || Math.abs(x - maxX) < EPS) &&
          wMax >= minY - EPS &&
          wMin <= maxY + EPS
        ) {
          wallIndices.push(i);
        }
      }
    });
    if (wallIndices.length === 0) return null;
    return { minX, minY, maxX, maxY, wallIndices };
  };

  const selectedWallsFormRect = () => {
    const idxs = selectedWallIndices();
    if (idxs.length < 4) return null;
    const walls = idxs.map((i) => (floor.beams || [])[i]).filter(Boolean);
    if (walls.length !== idxs.length) return null;

    // Every wall must be horizontal or vertical.
    const EPS = 0.01;
    for (const w of walls) {
      const isH = Math.abs(w.y1 - w.y2) < EPS;
      const isV = Math.abs(w.x1 - w.x2) < EPS;
      if (!isH && !isV) return null;
    }
    // Bounding box
    const xs = walls.flatMap((w) => [w.x1, w.x2]);
    const ys = walls.flatMap((w) => [w.y1, w.y2]);
    const minX = Math.min(...xs),
      maxX = Math.max(...xs);
    const minY = Math.min(...ys),
      maxY = Math.max(...ys);
    const width = maxX - minX;
    const height = maxY - minY;
    if (width < 0.5 || height < 0.5) return null;

    // Coverage check (Stage 4c2): each of the 4 sides must be FULLY
    // covered by wall segments. Works with segmented walls (e.g. top
    // side = beam_A (0..2) + beam_B (2..5) fully cover 0..5). Small
    // gaps up to GAP_TOL are tolerated (from imperfect drawing).
    const onSide = (val, target) => Math.abs(val - target) < EPS;
    const GAP_TOL = 0.05;
    const coverageOK = (segs, uMin, uMax) => {
      if (segs.length === 0) return false;
      // segs is an array of [start, end] with start <= end
      const sorted = segs
        .map(([a, b]) => [Math.min(a, b), Math.max(a, b)])
        .sort((s, t) => s[0] - t[0]);
      let cursor = uMin;
      for (const [s, e] of sorted) {
        if (e < cursor - GAP_TOL) continue; // fully before, ignore
        if (s > cursor + GAP_TOL) return false; // gap
        if (e > cursor) cursor = e;
        if (cursor >= uMax - GAP_TOL) return true; // full coverage reached
      }
      return cursor >= uMax - GAP_TOL;
    };

    // Collect segments per side.
    const topSegs = walls
      .filter((w) => onSide(w.y1, maxY) && onSide(w.y2, maxY))
      .map((w) => [w.x1, w.x2]);
    const bottomSegs = walls
      .filter((w) => onSide(w.y1, minY) && onSide(w.y2, minY))
      .map((w) => [w.x1, w.x2]);
    const leftSegs = walls
      .filter((w) => onSide(w.x1, minX) && onSide(w.x2, minX))
      .map((w) => [w.y1, w.y2]);
    const rightSegs = walls
      .filter((w) => onSide(w.x1, maxX) && onSide(w.x2, maxX))
      .map((w) => [w.y1, w.y2]);

    if (!coverageOK(topSegs, minX, maxX)) return null;
    if (!coverageOK(bottomSegs, minX, maxX)) return null;
    if (!coverageOK(leftSegs, minY, maxY)) return null;
    if (!coverageOK(rightSegs, minY, maxY)) return null;

    return { minX, minY, width, height };
  };

  const convertSelectionToRoom = (roomType) => {
    // If DETECT_ROOM populated a bbox, use that directly. Otherwise
    // fall back to the manual coverage-based check.
    let rect;
    if (detectedRoomBbox) {
      rect = {
        minX: detectedRoomBbox.minX,
        minY: detectedRoomBbox.minY,
        width: detectedRoomBbox.maxX - detectedRoomBbox.minX,
        height: detectedRoomBbox.maxY - detectedRoomBbox.minY,
      };
    } else {
      rect = selectedWallsFormRect();
      if (!rect) {
        window.alert(
          "Selected walls don't form a closed axis-aligned rectangle. " +
            "Pick the 4 (or more) walls that surround one room."
        );
        return;
      }
    }
    // Build the new room element — mirrors makeEmptyDrawing()'s shape.
    // BALCONY has different opening rules but same x/y/width/height, so
    // we can build a generic room and set type. User fills doors/windows
    // via Edit if wanted.
    const nextName = `${roomType.toLowerCase()}_${
      (floor.rooms || []).filter((r) => r.type === roomType).length + 1
    }`;
    const newRoom = {
      name: nextName,
      label: nextName,
      type: roomType,
      x: +rect.minX.toFixed(3),
      y: +rect.minY.toFixed(3),
      width: +rect.width.toFixed(3),
      height: +rect.height.toFixed(3),
      doors: [],
      windows: [],
      open_doors: [],
    };

    // Stage 4f: KEEP the walls after conversion (option a). Walls that
    // form room boundaries are shared with neighbouring rooms, so
    // deleting them would break Detect Room for the neighbour. The
    // walls stay in the list; the room element sits on top of them.
    commitFloor({
      ...floor,
      rooms: [...(floor.rooms || []), newRoom],
    });
    setSelection([]);
    setDetectedRoomBbox(null);
    // If DETECT_ROOM was active, deselect the tool so the next click
    // opens edit forms normally (not another detect attempt).
    if (selectedType === "DETECT_ROOM") {
      setSelectedType(null);
    }
  };

  // ── Convert selection to STAIRS (Stage 4k) ─────────────────────────
  // Same bbox source as room conversion. Emits a new STAIRS element
  // with sensible defaults; user edits step count / up_dir / width_dir
  // via the existing Stairs edit form afterwards.
  //   * width_dir: long side of the bbox — steps run across the long side
  //   * width:     the SHORT side (flight width)
  //   * depth:     0.3m (typical step depth)
  //   * steps:     bbox long-side / depth, rounded (min 3)
  //   * up_dir:    default "north" — user edits if wrong
  const convertSelectionToStairs = () => {
    let rect;
    if (detectedRoomBbox) {
      rect = {
        minX: detectedRoomBbox.minX,
        minY: detectedRoomBbox.minY,
        width: detectedRoomBbox.maxX - detectedRoomBbox.minX,
        height: detectedRoomBbox.maxY - detectedRoomBbox.minY,
      };
    } else {
      rect = selectedWallsFormRect();
      if (!rect) {
        window.alert(
          "Selected walls don't form a closed axis-aligned rectangle."
        );
        return;
      }
    }
    const DEFAULT_DEPTH = 0.3;
    // Long side = direction stairs go (width_dir). Short side = flight width.
    let widthDir, flightLength, flightWidth;
    if (rect.width >= rect.height) {
      widthDir = "x";
      flightLength = rect.width;
      flightWidth = rect.height;
    } else {
      widthDir = "y";
      flightLength = rect.height;
      flightWidth = rect.width;
    }
    const steps = Math.max(3, Math.round(flightLength / DEFAULT_DEPTH));
    const nextName = `stairs_${(floor.stairs || []).length + 1}`;
    const newStairs = {
      name: nextName,
      label: nextName,
      x: +rect.minX.toFixed(3),
      y: +rect.minY.toFixed(3),
      steps: steps,
      width: +flightWidth.toFixed(3),
      width_dir: widthDir,
      depth: DEFAULT_DEPTH,
      up_dir: "north", // user edits if wrong
      doors: [],
      open_doors: [],
    };
    commitFloor({
      ...floor,
      stairs: [...(floor.stairs || []), newStairs],
    });
    setSelection([]);
    setDetectedRoomBbox(null);
    if (selectedType === "DETECT_ROOM") {
      setSelectedType(null);
    }
  };

  // ── Convert selection to SHAFT (Stage 4l) ──────────────────────────
  // Indoor U-shape stair shaft with 2 parallel flights + landing.
  // Same bbox source as Stairs. Sensible defaults chosen from bbox
  // dimensions — user edits step count / up_dir / step_width / flat
  // via the existing Shaft edit form.
  //
  // Layout (for width_dir = "y", long side is Y):
  //   step_width = (short_side - separation) / 2   (each flight width)
  //   depth      = 0.3m per step
  //   separation = 0.4m between the 2 flights
  //   flat       = 0.95m landing depth
  //   steps      = 22 (typical residential floor)
  //   up_dir     = "north" (user edits if wrong)
  const convertSelectionToShaft = () => {
    let rect;
    if (detectedRoomBbox) {
      rect = {
        minX: detectedRoomBbox.minX,
        minY: detectedRoomBbox.minY,
        width: detectedRoomBbox.maxX - detectedRoomBbox.minX,
        height: detectedRoomBbox.maxY - detectedRoomBbox.minY,
      };
    } else {
      rect = selectedWallsFormRect();
      if (!rect) {
        window.alert(
          "Selected walls don't form a closed axis-aligned rectangle."
        );
        return;
      }
    }
    const DEFAULT_DEPTH = 0.3;
    const DEFAULT_SEPARATION = 0.4;
    const DEFAULT_FLAT = 0.95;
    const DEFAULT_STEPS = 22;

    // Long side = direction stairs go up (width_dir). Short side splits
    // into 2 flight widths + separation.
    let widthDir, shortSide;
    if (rect.height >= rect.width) {
      widthDir = "y";
      shortSide = rect.width;
    } else {
      widthDir = "x";
      shortSide = rect.height;
    }
    const stepWidth = Math.max(
      0.5,
      +((shortSide - DEFAULT_SEPARATION) / 2).toFixed(3)
    );

    const nextName = `sshaft_${(floor.shafts || []).length + 1}`;
    const newShaft = {
      name: nextName,
      label: nextName,
      x: +rect.minX.toFixed(3),
      y: +rect.minY.toFixed(3),
      steps: DEFAULT_STEPS,
      step_width: stepWidth,
      width_dir: widthDir,
      depth: DEFAULT_DEPTH,
      separation: DEFAULT_SEPARATION,
      flat: DEFAULT_FLAT,
      up_dir: "north",
      doors: [],
      windows: [],
      open_doors: [],
      elevators: [],
    };
    commitFloor({
      ...floor,
      shafts: [...(floor.shafts || []), newShaft],
    });
    setSelection([]);
    setDetectedRoomBbox(null);
    if (selectedType === "DETECT_ROOM") {
      setSelectedType(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  //  MIRROR SELECTED ELEMENTS (Stage 5a)
  // ═══════════════════════════════════════════════════════════════════
  //
  // Ctrl+click any mix of rooms / walls / customs / shafts / stairs,
  // then mirror them across a vertical (x=m) or horizontal (y=m) line.
  // Originals stay untouched — NEW mirrored copies are added, named
  // "<name>_m" (or "_m2", "_m3", ... on collision).
  //
  // Door/window/open_door openings on rooms & shafts/stairs are mirrored
  // properly: the wall label swaps (west↔east for vertical mirror,
  // north↔south for horizontal), the position along the wall is flipped,
  // and swing direction (left↔right) is flipped to match the reflection.
  // For SHAFT/STAIRS, `rotation` is also flipped (cw↔ccw) since any
  // single-axis mirror reverses the climb direction's handedness.
  // For CUSTOM polygons, points are mirrored + point order reversed to
  // preserve CCW winding, but openings are DROPPED (edge-index mapping
  // after a winding reversal is unreliable) — user re-adds them on the
  // mirrored copy.

  const uniqueMirroredName = (baseName, existingNames) => {
    let candidate = `${baseName}_m`;
    let n = 2;
    while (existingNames.has(candidate)) {
      candidate = `${baseName}_m${n}`;
      n += 1;
    }
    return candidate;
  };

  // Mirror the openings (doors/windows/open_doors) of a rectangle-like
  // element (ROOM, or SHAFT/STAIRS derived block) across the chosen axis.
  //   width, height — the rectangle's extents (unchanged by the mirror)
  //   axis           — "vertical" | "horizontal"
  const mirrorRectOpenings = (elem, width, height, axis) => {
    const swapWalls =
      axis === "vertical"
        ? { west: "east", east: "west", north: "north", south: "south" }
        : { north: "south", south: "north", west: "west", east: "east" };
    const flipPositionWalls =
      axis === "vertical"
        ? new Set(["north", "south"])
        : new Set(["west", "east"]);
    const wallLenFor = (beamName) =>
      ["south", "north"].includes(beamName) ? width : height;

    // Stage 8a: door mirroring needs the SAME (hinge_side, swing) tables
    // ElementForm.jsx uses when placing a door by rotation — mirrors of
    // swingFromRotation() / hingeFromRotation() there. A mirror reflection
    // always reverses handedness (cw↔ccw); swing follows that flip
    // directly, but hinge_side depends on BOTH the wall AND the rotation,
    // so it must be re-derived on the NEW wall from the FLIPPED rotation
    // — simply flipping swing while leaving hinge_side untouched (the
    // old approach) only happened to work for west/east by coincidence;
    // it produced invalid combinations for north/south doors, putting
    // them outside the room.
    const HINGE_TABLE = {
      south: { clockwise: "start", counterclockwise: "end" },
      north: { clockwise: "end", counterclockwise: "start" },
      west: { clockwise: "end", counterclockwise: "start" },
      east: { clockwise: "start", counterclockwise: "end" },
    };
    const hingeFromRotation = (wall, rotation, isMain) => {
      const s = HINGE_TABLE[wall]?.[rotation] || "start";
      if (!isMain) return s;
      return s === "start" ? "end" : "start"; // main doors invert
    };
    const swingFromRotation = (rotation) =>
      rotation === "clockwise" ? "right" : "left";
    // Inverse of HINGE_TABLE, per wall — given the RAW (pre-isMain-flip)
    // hinge label, what rotation produced it?
    const ROTATION_TABLE = {
      south: { start: "clockwise", end: "counterclockwise" },
      north: { end: "clockwise", start: "counterclockwise" },
      west: { end: "clockwise", start: "counterclockwise" },
      east: { start: "clockwise", end: "counterclockwise" },
    };
    const rotationFromHinge = (wall, hingeSide, isMain) => {
      const raw = isMain
        ? hingeSide === "start"
          ? "end"
          : "start"
        : hingeSide;
      return ROTATION_TABLE[wall]?.[raw] || "clockwise";
    };

    const mirrorDoor = (o) => {
      const oldWall = o.wall;
      const newWall = swapWalls[oldWall] || oldWall;
      const isMain = !!o.is_main;

      // 1) What rotation produced the CURRENT hinge_side on the old wall?
      const rotation = rotationFromHinge(oldWall, o.hinge_side, isMain);
      // 2) Any single-axis mirror reverses handedness — flip it.
      const newRotation =
        rotation === "clockwise" ? "counterclockwise" : "clockwise";
      // 3) Re-derive hinge_side + swing on the NEW wall from the FLIPPED
      //    rotation — this is the correct combination, not a guess.
      const newHingeSide = hingeFromRotation(newWall, newRotation, isMain);
      const newSwing = swingFromRotation(newRotation);

      // 4) Recompute the gap position. If this wall's axis is flipped by
      //    the mirror, mirror the [start,end] interval along the wall
      //    then re-express it using the NEW hinge_side. If the axis is
      //    untouched (wall only relabelled, e.g. west/east under a
      //    vertical mirror) the interval itself doesn't move — just
      //    re-express it if the hinge_side label changed.
      const oldStart =
        o.hinge_side === "start" ? o.position : o.position - o.width;
      const oldEnd =
        o.hinge_side === "start" ? o.position + o.width : o.position;
      let position;
      if (flipPositionWalls.has(oldWall)) {
        const L = wallLenFor(oldWall);
        const newStart = L - oldEnd;
        const newEnd = L - oldStart;
        position = newHingeSide === "start" ? newStart : newEnd;
      } else {
        position = newHingeSide === "start" ? oldStart : oldEnd;
      }

      return {
        ...o,
        wall: newWall,
        position: +position.toFixed(3),
        swing: newSwing,
        hinge_side: newHingeSide,
      };
    };
    const mirrorWindow = (o) => {
      const newWall = swapWalls[o.wall] || o.wall;
      let position = o.position;
      if (flipPositionWalls.has(o.wall)) {
        const L = wallLenFor(o.wall);
        position = L - o.position;
      }
      return { ...o, wall: newWall, position: +position.toFixed(3) };
    };
    const mirrorOpenDoor = (o) => {
      const newWall = swapWalls[o.wall] || o.wall;
      let position = o.position;
      if (flipPositionWalls.has(o.wall)) {
        const L = wallLenFor(o.wall);
        const oldStart = o.position,
          oldEnd = o.position + o.width;
        position = L - oldEnd;
      }
      return { ...o, wall: newWall, position: +position.toFixed(3) };
    };

    return {
      doors: (elem.doors || []).map(mirrorDoor),
      windows: (elem.windows || []).map(mirrorWindow),
      open_doors: (elem.open_doors || []).map(mirrorOpenDoor),
    };
  };

  const applyMirror = (axis, line) => {
    const m = Number(line) || 0;
    const next = {
      rooms: [...(floor.rooms || [])],
      beams: [...(floor.beams || [])],
      customs: [...(floor.customs || [])],
      shafts: [...(floor.shafts || [])],
      stairs: [...(floor.stairs || [])],
    };
    // Collect every name currently in use across all arrays so mirrored
    // copies never collide.
    const existingNames = new Set([
      ...next.rooms.map((r) => r.name),
      ...next.beams.map((w) => w.name),
      ...next.customs.map((c) => c.name),
      ...next.shafts.map((s) => s.name),
      ...next.stairs.map((s) => s.name),
    ]);

    let mirroredCount = 0;
    let droppedOpeningsCount = 0;

    for (const sel of selection) {
      if (sel.kind === "room") {
        const r = (floor.rooms || [])[sel.index];
        if (!r) continue;
        const newX = axis === "vertical" ? 2 * m - (r.x + r.width) : r.x;
        const newY = axis === "horizontal" ? 2 * m - (r.y + r.height) : r.y;
        const openings = mirrorRectOpenings(r, r.width, r.height, axis);
        const name = uniqueMirroredName(r.name, existingNames);
        existingNames.add(name);
        next.rooms.push({
          ...r,
          name,
          label: name,
          x: +newX.toFixed(3),
          y: +newY.toFixed(3),
          ...openings,
        });
        mirroredCount += 1;
      } else if (sel.kind === "beam") {
        const w = (floor.beams || [])[sel.index];
        if (!w) continue;
        const nx1 = axis === "vertical" ? 2 * m - w.x1 : w.x1;
        const ny1 = axis === "horizontal" ? 2 * m - w.y1 : w.y1;
        const nx2 = axis === "vertical" ? 2 * m - w.x2 : w.x2;
        const ny2 = axis === "horizontal" ? 2 * m - w.y2 : w.y2;
        const name = uniqueMirroredName(w.name, existingNames);
        existingNames.add(name);
        next.beams.push({
          ...w,
          name,
          x1: +nx1.toFixed(3),
          y1: +ny1.toFixed(3),
          x2: +nx2.toFixed(3),
          y2: +ny2.toFixed(3),
        });
        mirroredCount += 1;
      } else if (sel.kind === "shaft") {
        const s = (floor.shafts || [])[sel.index];
        if (!s) continue;
        const der = shaftDerived(s);
        const newX = axis === "vertical" ? 2 * m - (s.x + der.blockW) : s.x;
        const newY = axis === "horizontal" ? 2 * m - (s.y + der.blockH) : s.y;
        const openings = mirrorRectOpenings(s, der.blockW, der.blockH, axis);
        const newRotation =
          s.rotation === "clockwise" ? "counterclockwise" : "clockwise";
        const name = uniqueMirroredName(s.name, existingNames);
        existingNames.add(name);
        next.shafts.push({
          ...s,
          name,
          label: name,
          x: +newX.toFixed(3),
          y: +newY.toFixed(3),
          rotation: newRotation,
          ...openings,
        });
        mirroredCount += 1;
      } else if (sel.kind === "stairs") {
        const s = (floor.stairs || [])[sel.index];
        if (!s) continue;
        const der = stairsDerived(s);
        const newX = axis === "vertical" ? 2 * m - (s.x + der.blockW) : s.x;
        const newY = axis === "horizontal" ? 2 * m - (s.y + der.blockH) : s.y;
        const openings = mirrorRectOpenings(s, der.blockW, der.blockH, axis);
        const newRotation =
          s.rotation === "clockwise" ? "counterclockwise" : "clockwise";
        const name = uniqueMirroredName(s.name, existingNames);
        existingNames.add(name);
        next.stairs.push({
          ...s,
          name,
          label: name,
          x: +newX.toFixed(3),
          y: +newY.toFixed(3),
          rotation: newRotation,
          ...openings,
        });
        mirroredCount += 1;
      } else if (sel.kind === "custom") {
        const c = (floor.customs || [])[sel.index];
        if (!c) continue;
        const mirroredPts = (c.points || []).map(([px, py]) => {
          const nx = axis === "vertical" ? 2 * m - px : px;
          const ny = axis === "horizontal" ? 2 * m - py : py;
          return [+nx.toFixed(3), +ny.toFixed(3)];
        });
        // Mirroring reverses winding — reverse point order to restore
        // the original CCW/CW convention the app expects.
        mirroredPts.reverse();
        const hadOpenings =
          (c.doors?.length || 0) +
            (c.windows?.length || 0) +
            (c.open_doors?.length || 0) >
          0;
        if (hadOpenings) droppedOpeningsCount += 1;
        const name = uniqueMirroredName(c.name, existingNames);
        existingNames.add(name);
        next.customs.push({
          ...c,
          name,
          label: name,
          points: mirroredPts,
          doors: [],
          windows: [],
          open_doors: [],
        });
        mirroredCount += 1;
      }
    }

    if (mirroredCount === 0) {
      window.alert("Nothing to mirror — select at least one element first.");
      return;
    }

    commitFloor({ ...floor, ...next });
    setSelection([]);
    setMirrorPanelOpen(false);

    if (droppedOpeningsCount > 0) {
      window.alert(
        `Mirrored ${mirroredCount} element(s). Note: ${droppedOpeningsCount} ` +
          `CUSTOM polygon(s) had doors/windows that could not be mirrored ` +
          `automatically — please re-add them on the mirrored copy.`
      );
    }
  };

  // ── Rubber-band (drag-box) selection (Stage 5c) ─────────────────────
  // Called by PlanCanvas after a real left-button drag on empty canvas
  // (only armed when no drawing tool is active). An element is selected
  // when it is FULLY inside the dragged rectangle — matches the common
  // "window select" convention. Ctrl+drag ADDS to the existing selection
  // instead of replacing it.
  const handleRubberBandSelect = (rect, mods = {}) => {
    const { minX, maxX, minY, maxY } = rect;
    const ctrl = !!mods.ctrl;
    const picked = [];

    (floor.rooms || []).forEach((r, i) => {
      if (
        r.x >= minX &&
        r.x + r.width <= maxX &&
        r.y >= minY &&
        r.y + r.height <= maxY
      ) {
        picked.push({ kind: "room", index: i });
      }
    });
    (floor.beams || []).forEach((w, i) => {
      if (
        w.x1 >= minX &&
        w.x1 <= maxX &&
        w.x2 >= minX &&
        w.x2 <= maxX &&
        w.y1 >= minY &&
        w.y1 <= maxY &&
        w.y2 >= minY &&
        w.y2 <= maxY
      ) {
        picked.push({ kind: "beam", index: i });
      }
    });
    (floor.columns || []).forEach((c, i) => {
      // include a column if its center is inside the box
      if (c.x >= minX && c.x <= maxX && c.y >= minY && c.y <= maxY) {
        picked.push({ kind: "column", index: i });
      }
    });
    (floor.customs || []).forEach((c, i) => {
      const pts = c.points || [];
      if (pts.length === 0) return;
      const inside = pts.every(
        ([px, py]) => px >= minX && px <= maxX && py >= minY && py <= maxY
      );
      if (inside) picked.push({ kind: "custom", index: i });
    });
    (floor.shafts || []).forEach((s, i) => {
      const der = shaftDerived(s);
      if (
        s.x >= minX &&
        s.x + der.blockW <= maxX &&
        s.y >= minY &&
        s.y + der.blockH <= maxY
      ) {
        picked.push({ kind: "shaft", index: i });
      }
    });
    (floor.stairs || []).forEach((s, i) => {
      const der = stairsDerived(s);
      if (
        s.x >= minX &&
        s.x + der.blockW <= maxX &&
        s.y >= minY &&
        s.y + der.blockH <= maxY
      ) {
        picked.push({ kind: "stairs", index: i });
      }
    });

    if (picked.length === 0 && !ctrl) {
      // Dragged an empty area with no modifier — clear selection, same
      // as clicking empty space.
      setSelection([]);
      setDetectedRoomBbox(null);
      return;
    }

    setDetectedRoomBbox(null);
    if (ctrl) {
      setSelection((prev) => {
        const merged = [...prev];
        for (const p of picked) {
          if (!merged.some((s) => s.kind === p.kind && s.index === p.index)) {
            merged.push(p);
          }
        }
        return merged;
      });
    } else {
      setSelection(picked);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  //  COPY / PASTE (Stage 5d)
  // ═══════════════════════════════════════════════════════════════════
  //
  // Copy captures the currently selected elements (deep-cloned) into an
  // independent `clipboard` state — NOT part of `floor`, so it survives
  // New Project / Open Project. Paste uses the same click-to-place-then-
  // adjust-then-commit flow as Mirror: click on canvas sets the target
  // position for the SW corner of the copied selection's bbox, fine-tune
  // with X/Y fields, then "Paste copy" adds new elements (unchanged
  // orientation — a pure translation, no flipping like Mirror does).

  // Compute the SW corner (minX, minY) of a set of {kind, data} items —
  // used both to capture the copy's reference point and, at paste time,
  // to compute the offset from the fine-tuned target position.
  const bboxMinOf = (items) => {
    let minX = Infinity,
      minY = Infinity;
    for (const { kind, data } of items) {
      if (kind === "room") {
        minX = Math.min(minX, data.x);
        minY = Math.min(minY, data.y);
      } else if (kind === "beam") {
        minX = Math.min(minX, data.x1, data.x2);
        minY = Math.min(minY, data.y1, data.y2);
      } else if (kind === "custom") {
        for (const [px, py] of data.points || []) {
          minX = Math.min(minX, px);
          minY = Math.min(minY, py);
        }
      } else if (kind === "shaft" || kind === "stairs") {
        minX = Math.min(minX, data.x);
        minY = Math.min(minY, data.y);
      }
    }
    return { minX: isFinite(minX) ? minX : 0, minY: isFinite(minY) ? minY : 0 };
  };

  // Stage 9a: Copy now also works while editing a SINGLE element via
  // the normal click-to-edit flow (editIndex/editKind set) — not just
  // via Ctrl+click/rubber-band multi-selection. If a real multi-
  // selection exists, that takes priority (matches prior behaviour);
  // otherwise falls back to whichever single element is currently open
  // for edit.
  const handleCopySelection = () => {
    const sourceSel =
      selection.length > 0
        ? selection
        : editIndex !== null && editKind
          ? [{ kind: editKind, index: editIndex }]
          : [];
    if (sourceSel.length === 0) return;
    const items = [];
    for (const sel of sourceSel) {
      let src;
      if (sel.kind === "room") src = (floor.rooms || [])[sel.index];
      if (sel.kind === "beam") src = (floor.beams || [])[sel.index];
      if (sel.kind === "custom") src = (floor.customs || [])[sel.index];
      if (sel.kind === "shaft") src = (floor.shafts || [])[sel.index];
      if (sel.kind === "stairs") src = (floor.stairs || [])[sel.index];
      if (!src) continue;
      items.push({ kind: sel.kind, data: JSON.parse(JSON.stringify(src)) });
    }
    if (items.length === 0) return;
    const { minX, minY } = bboxMinOf(items);
    setClipboard({ items, refX: minX, refY: minY });
    setPasteX(minX);
    setPasteY(minY);
    // Copying and mirroring are mutually exclusive click-intercepts —
    // close the mirror panel if it happened to be open.
    setMirrorPanelOpen(false);
  };

  const uniqueName = (baseName, existingNames, suffix) => {
    let candidate = `${baseName}${suffix}`;
    let n = 2;
    while (existingNames.has(candidate)) {
      candidate = `${baseName}${suffix}${n}`;
      n += 1;
    }
    return candidate;
  };

  const applyPaste = () => {
    if (!clipboard || clipboard.items.length === 0) return;
    const dx = (Number(pasteX) || 0) - clipboard.refX;
    const dy = (Number(pasteY) || 0) - clipboard.refY;

    const next = {
      rooms: [...(floor.rooms || [])],
      beams: [...(floor.beams || [])],
      customs: [...(floor.customs || [])],
      shafts: [...(floor.shafts || [])],
      stairs: [...(floor.stairs || [])],
    };
    const existingNames = new Set([
      ...next.rooms.map((r) => r.name),
      ...next.beams.map((w) => w.name),
      ...next.customs.map((c) => c.name),
      ...next.shafts.map((s) => s.name),
      ...next.stairs.map((s) => s.name),
    ]);

    for (const { kind, data } of clipboard.items) {
      if (kind === "room") {
        const name = uniqueName(data.name, existingNames, "_copy");
        existingNames.add(name);
        next.rooms.push({
          ...data,
          name,
          label: name,
          x: +(data.x + dx).toFixed(3),
          y: +(data.y + dy).toFixed(3),
        });
      } else if (kind === "beam") {
        const name = uniqueName(data.name, existingNames, "_copy");
        existingNames.add(name);
        next.beams.push({
          ...data,
          name,
          x1: +(data.x1 + dx).toFixed(3),
          y1: +(data.y1 + dy).toFixed(3),
          x2: +(data.x2 + dx).toFixed(3),
          y2: +(data.y2 + dy).toFixed(3),
        });
      } else if (kind === "custom") {
        const name = uniqueName(data.name, existingNames, "_copy");
        existingNames.add(name);
        next.customs.push({
          ...data,
          name,
          label: name,
          points: (data.points || []).map(([px, py]) => [
            +(px + dx).toFixed(3),
            +(py + dy).toFixed(3),
          ]),
        });
      } else if (kind === "shaft") {
        const name = uniqueName(data.name, existingNames, "_copy");
        existingNames.add(name);
        next.shafts.push({
          ...data,
          name,
          label: name,
          x: +(data.x + dx).toFixed(3),
          y: +(data.y + dy).toFixed(3),
        });
      } else if (kind === "stairs") {
        const name = uniqueName(data.name, existingNames, "_copy");
        existingNames.add(name);
        next.stairs.push({
          ...data,
          name,
          label: name,
          x: +(data.x + dx).toFixed(3),
          y: +(data.y + dy).toFixed(3),
        });
      }
    }

    commitFloor({ ...floor, ...next });
    // Stage 9b: matches Mirror's behaviour exactly — clean "done" feel.
    // Clipboard itself stays intact (you can click "Paste…" again to
    // stamp another copy), but the panel closes and selection clears
    // instead of staying open on the just-pasted items.
    setSelection([]);
    setDetectedRoomBbox(null);
    setPastePanelOpen(false);
  };

  // Delete key — priorities:
  //   1) if multi-select has items → delete them all in one undo entry
  //   2) else if a single element is being edited → delete it
  //   3) else → do nothing
  // Ignored when a form field is focused so typing a decimal (Delete
  // inside <input>) is not intercepted. Goes through commitFloor so
  // Ctrl+Z recovers.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Delete") return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (selection.length > 0) {
        e.preventDefault();
        deleteSelected();
        return;
      }
      if (editIndex !== null && editKind !== null) {
        e.preventDefault();
        handleDeleteElement(editIndex, editKind);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, editIndex, editKind, floor]);

  // Change the selected type AND create the drawing in one step so the
  // child form never sees selectedType!=null with drawing==null.
  const handleSelectType = (t) => {
    setSelectedType(t);
    if (t) {
      // CLOSE_OPENING reuses the WALL drawing shape + isBeam handlers,
      // but with a marker flag so click handler snaps to existing wall
      // endpoints instead of the grid.
      if (t === "CLOSE_OPENING") {
        setDrawing({
          type: "BEAM",
          isBeam: true,
          isCloseOpening: true,
          name: "",
          label: "",
          x1: 0,
          y1: 0,
          x2: 0,
          y2: 0,
          wallPhase: "p1",
        });
      } else if (t === "DETECT_ROOM") {
        // DETECT_ROOM has NO drawing object — a click on canvas triggers
        // the auto-detect algorithm which populates the multi-selection
        // with the room's walls. No form is needed.
        setDrawing(null);
      } else {
        setDrawing(makeEmptyDrawing(t));
      }
      setFirstCorner(null);
      setOpeningMode(null);
      setOpeningDraft(null);
    } else {
      setDrawing(null);
    }
  };

  // ── Keyboard shortcuts for CUSTOM polygon phase ────────────────────
  // Enter = close polygon, O = keep open.
  // (Stage 4m: ESC removed here — global handler below now does full
  // cancel for every tool including CUSTOM, matching user expectation.)
  React.useEffect(() => {
    if (!drawing?.isCustom || drawing.polygonPhase !== "points") return;
    const onKey = (e) => {
      // ignore key events fired inside form inputs (typing decimals)
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (e.key === "Enter") {
        e.preventDefault();
        if ((drawing.points?.length || 0) >= 3) {
          setDrawing({ ...drawing, closed: true, polygonPhase: "openings" });
        }
      } else if (e.key === "o" || e.key === "O") {
        e.preventDefault();
        if ((drawing.points?.length || 0) >= 2) {
          setDrawing({ ...drawing, closed: false, polygonPhase: "openings" });
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing]);

  // Stage 4m: universal ESC handler — one rule for every tool.
  // ESC fully cancels whatever is happening. Priority:
  //   1) if placing an opening (door/window/open door) → cancel opening
  //   2) else if editing an existing element         → cancel edit
  //   3) else if a type is selected in the picker    → fully cancel
  //      (deselects the tool + drops any in-progress drawing/points)
  //   4) else if multi-selection has items           → clear selection
  //   5) else                                         → do nothing
  //
  // Stage 4p: attach in the CAPTURE phase (3rd arg = true) so this
  // handler runs BEFORE the event reaches a focused <input type="number">.
  // Some browsers intercept Escape on number inputs to reset the typed
  // value before it bubbles up — capture phase guarantees we see it first
  // and can cancel the tool in one press regardless of focus.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;

      // Stage 6f: an uncommitted group move/resize takes top priority —
      // ESC discards it (snaps back to original) without touching
      // anything else, same as clicking its "Cancel" button.
      if (groupPendingChange) {
        e.preventDefault();
        e.stopPropagation();
        handleCancelGroupChange();
        return;
      }
      if (openingMode || openingDraft) {
        e.preventDefault();
        e.stopPropagation();
        setOpeningMode(null);
        setOpeningDraft(null);
        return;
      }
      if (editIndex !== null) {
        e.preventDefault();
        e.stopPropagation();
        handleCancelEdit();
        return;
      }
      if (selectedType) {
        e.preventDefault();
        e.stopPropagation();
        // Blur any focused input so the cancel is visually complete.
        if (
          document.activeElement &&
          typeof document.activeElement.blur === "function"
        ) {
          document.activeElement.blur();
        }
        resetToolState();
        return;
      }
      if (selection.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        setSelection([]);
        return;
      }
    };
    window.addEventListener("keydown", onKey, true); // capture phase
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drawing,
    openingMode,
    openingDraft,
    editIndex,
    selectedType,
    selection,
    groupPendingChange,
  ]);

  // ── Canvas click dispatch ─────────────────────────────────────────
  //
  // Stage 3v: the old guard rejected ANY click with x<0 or y<0, but
  // users legitimately draw at negative coordinates (before the shift
  // feature exists). The guard was originally added to catch runaway
  // world coords like Y=1684 from a collapsed SVG, so we KEEP that
  // spirit but only reject clearly-insane values — anything within a
  // reasonable margin around the bbox is accepted, including negative
  // coords. This lets single-click, Ctrl+click, and new-element clicks
  // all work on the negative side of the origin.
  const isSaneClick = (x, y) => {
    const margin = Math.max(bbox.width, bbox.height, 50);
    // Measure distance from the CONTENT's own offset (its own SW
    // corner), not from absolute world zero — a file whose content
    // legitimately sits far from the origin (e.g. real survey/DXF
    // coordinates) would otherwise have every click on it rejected as
    // "insane", even after the canvas itself renders correctly.
    return (
      Math.abs(x - (bbox.offsetX || 0)) <= bbox.width + margin &&
      Math.abs(y - (bbox.offsetY || 0)) <= bbox.height + margin
    );
  };

  // Is world point (px,py) inside column c's rectangle (+ optional margin)?
  // Transforms the point into the column's local (un-rotated) frame and does
  // a box test. `margin` (metres) enlarges the hit area so small columns are
  // easy to click.
  const pointInColumn = (px, py, c, margin = 0) => {
    if (!c) return false;
    const w = c.width,
      t = c.thickness;
    let hw, hh, ang;
    if (c.direction === "y") {
      hw = t / 2;
      hh = w / 2;
      ang = 0;
    } else if (c.direction === "angled") {
      hw = w / 2;
      hh = t / 2;
      ang = ((c.angle || 0) * Math.PI) / 180;
    } else {
      hw = w / 2;
      hh = t / 2;
      ang = 0;
    }
    const dx = px - c.x,
      dy = py - c.y;
    const ca = Math.cos(-ang),
      sa = Math.sin(-ang);
    const lx = dx * ca - dy * sa;
    const ly = dx * sa + dy * ca;
    return Math.abs(lx) <= hw + margin && Math.abs(ly) <= hh + margin;
  };

  const handleCanvasClick = (x, y, mods = {}) => {
    if (!isSaneClick(x, y)) {
      // extremely large value — likely a runaway coord from a broken
      // SVG state; silently ignore.
      return;
    }
    const ctrl = !!mods.ctrl;

    // ── Mirror line picking (Stage 5b) ──────────────────────────────────
    // While the Mirror panel is open, a plain (non-Ctrl) canvas click
    // sets the mirror line: the clicked X for a vertical mirror, or the
    // clicked Y for a horizontal mirror. Ctrl+click still falls through
    // to normal multi-select toggling so the user can adjust which
    // elements are included while the panel stays open.
    if (mirrorPanelOpen && !ctrl) {
      setMirrorLine(mirrorAxis === "vertical" ? +x.toFixed(3) : +y.toFixed(3));
      return;
    }

    // ── Paste position picking (Stage 5d) ───────────────────────────────
    // While the Paste panel is open, a plain (non-Ctrl) canvas click sets
    // the target position for the SW corner of the copied selection —
    // same feel as Mirror's click-to-place-the-line. Ctrl+click still
    // falls through to normal multi-select toggling.
    if (pastePanelOpen && !ctrl) {
      setPasteX(+x.toFixed(3));
      setPasteY(+y.toFixed(3));
      return;
    }

    // ── DETECT_ROOM tool (Stage 4e) ────────────────────────────────────
    // Click inside empty room area → algorithm finds the enclosing
    // rectangle by walking 4 directions until it hits a wall, then
    // populates the multi-selection with all walls on those 4 sides.
    // The Convert-to-room yellow bar then appears in the list.
    if (selectedType === "DETECT_ROOM") {
      const found = detectRoomAt(x, y);
      if (!found) {
        window.alert(
          "Couldn't find a room here. Make sure you clicked " +
            "inside a fully-enclosed space with walls on all 4 sides."
        );
        return;
      }
      // Populate the multi-selection with the detected walls.
      setSelection(found.wallIndices.map((i) => ({ kind: "beam", index: i })));
      // Also remember the detected bbox so the Convert bar can use it
      // directly (skipping the coverage-recompute that fails when walls
      // extend beyond the detected room boundary).
      setDetectedRoomBbox({
        minX: found.minX,
        minY: found.minY,
        maxX: found.maxX,
        maxY: found.maxY,
        wallIndices: found.wallIndices,
      });
      return;
    }

    // ── Multi-select via Ctrl+click (Stage 3u ship 1) ─────────────────
    //
    // Ctrl+click toggles the hit element in/out of the multi-selection,
    // WITHOUT opening the edit form. Requires the user not to be in the
    // middle of drawing something (an element type selected in the
    // right panel is treated as "draw mode" so we don't hijack clicks).
    // A plain click without Ctrl continues to open edit as before.
    if (
      ctrl &&
      !selectedType &&
      editIndex === null &&
      !drawing?.isCustom &&
      !drawing?.isShaft &&
      !drawing?.isStairs &&
      !drawing?.isBeam &&
      !drawing?.isColumn
    ) {
      // Text labels FIRST — they're small and can sit on top of walls;
      // give the user a chance to hit them. Uses an approximate bounding
      // box (chars * 0.15m wide, 0.35m tall) around the text's world
      // position. Only fires when showTexts is on (otherwise texts are
      // invisible and shouldn't be selectable).
      if (showTexts) {
        const hitText = (floor.texts || []).findIndex((t) => {
          if (!t || typeof t.x !== "number" || typeof t.y !== "number") {
            return false;
          }
          const label = t.content || "";
          const halfW = Math.max(0.4, label.length * 0.1);
          const halfH = 0.25;
          return Math.abs(x - t.x) <= halfW && Math.abs(y - t.y) <= halfH;
        });
        if (hitText !== -1) {
          toggleSelection("text", hitText);
          return;
        }
      }
      // Hit-test same order as click-to-edit below.
      const hitRoom = floor.rooms.findIndex(
        (r) => x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height
      );
      if (hitRoom !== -1) {
        toggleSelection("room", hitRoom);
        return;
      }
      const hitCustom = (floor.customs || []).findIndex((c) =>
        pointInPolygon(x, y, c.points || [])
      );
      if (hitCustom !== -1) {
        toggleSelection("custom", hitCustom);
        return;
      }
      const hitShaft = (floor.shafts || []).findIndex((s) => {
        const der = shaftDerived(s);
        return (
          x >= s.x && x <= s.x + der.blockW && y >= s.y && y <= s.y + der.blockH
        );
      });
      if (hitShaft !== -1) {
        toggleSelection("shaft", hitShaft);
        return;
      }
      const hitStairs = (floor.stairs || []).findIndex((s) => {
        const der = stairsDerived(s);
        return (
          x >= s.x && x <= s.x + der.blockW && y >= s.y && y <= s.y + der.blockH
        );
      });
      if (hitStairs !== -1) {
        toggleSelection("stairs", hitStairs);
        return;
      }
      const hitColumn = (floor.columns || []).findIndex((c) =>
        pointInColumn(x, y, c, 0.15)
      );
      if (hitColumn !== -1) {
        toggleSelection("column", hitColumn);
        return;
      }
      const hitWall = (floor.beams || []).findIndex((w) => {
        const dx = w.x2 - w.x1,
          dy = w.y2 - w.y1;
        const L2 = dx * dx + dy * dy;
        if (L2 < 1e-6) return false;
        let t = ((x - w.x1) * dx + (y - w.y1) * dy) / L2;
        if (t < 0) t = 0;
        if (t > 1) t = 1;
        const px = w.x1 + t * dx,
          py = w.y1 + t * dy;
        return Math.hypot(x - px, y - py) <= 0.3;
      });
      if (hitWall !== -1) {
        toggleSelection("beam", hitWall);
        return;
      }
      // Ctrl+click on empty space → clear selection
      clearSelection();
      return;
    }

    // Plain click on canvas — if there IS a selection but nothing is
    // being edited/drawn, clear the selection so the click doesn't
    // feel "sticky". Then fall through to the normal handling below.
    if (!ctrl && selection.length > 0 && editIndex === null && !selectedType) {
      setSelection([]);
      // continue to normal click handling below
    }

    // If we're placing an opening, that takes priority.
    if (openingMode && drawing) {
      dispatchOpeningClick(x, y);
      return;
    }

    // CUSTOM polygon point placement — Stage 3a
    if (drawing?.isCustom && drawing.polygonPhase === "points") {
      // Snap: existing polygon point → axis-aligned → grid.
      const snapped = snapCustomPoint(x, y, drawing.points);
      setDrawing({
        ...drawing,
        points: [...drawing.points, [snapped.x, snapped.y]],
      });
      return;
    }

    // SHAFT SW corner placement — Stage 3c-1
    if (drawing?.isShaft) {
      // Single click seeds the SW corner. The rectangle is COMPUTED from
      // the shape parameters, so we just record x, y and let the form
      // show the block dimensions.
      setDrawing({ ...drawing, x, y });
      return;
    }

    // STAIRS SW corner placement — Stage 3h
    if (drawing?.isStairs) {
      setDrawing({ ...drawing, x, y });
      return;
    }

    // COLUMN placement — Stage 5. One click sets the CENTER (grid+axis
    // snapped like walls). The form then shows the size/direction so the
    // user can adjust before adding.
    if (drawing?.isColumn) {
      const snapped = snapCustomPoint(x, y, []);
      setDrawing({
        ...drawing,
        x: snapped.x,
        y: snapped.y,
        columnPhase: "done",
      });
      return;
    }

    // WALL two-endpoint placement — Stage 3j
    // CLOSE_OPENING — same as WALL but snaps to existing wall endpoints
    // within 0.3m instead of grid snap. Great for bridging door/window gaps.
    if (drawing?.isBeam) {
      let snapX, snapY;
      if (drawing.isCloseOpening) {
        // Find nearest wall endpoint within 0.3m
        const SNAP_DIST = 0.3;
        let best = null;
        let bestD = SNAP_DIST + 1e-9;
        for (const w of floor.beams || []) {
          for (const [ex, ey] of [
            [w.x1, w.y1],
            [w.x2, w.y2],
          ]) {
            const d = Math.hypot(x - ex, y - ey);
            if (d < bestD) {
              bestD = d;
              best = [ex, ey];
            }
          }
        }
        if (best) {
          snapX = best[0];
          snapY = best[1];
        } else {
          snapX = x;
          snapY = y;
        }
      } else {
        // Regular WALL — grid + axis snap
        const prev =
          drawing.wallPhase === "p2" ? [[drawing.x1, drawing.y1]] : [];
        const snapped = snapCustomPoint(x, y, prev);
        snapX = snapped.x;
        snapY = snapped.y;
      }

      if (drawing.wallPhase === "p1") {
        setDrawing({
          ...drawing,
          x1: snapX,
          y1: snapY,
          x2: snapX,
          y2: snapY, // seed so preview shows something
          wallPhase: "p2",
        });
      } else if (drawing.wallPhase === "p2") {
        // CLOSE_OPENING: auto-commit immediately, no form needed.
        if (drawing.isCloseOpening) {
          const ax = drawing.x1,
            ay = drawing.y1; // first endpoint
          const bx = snapX,
            by = snapY; // second endpoint
          // Reject zero-length
          if (Math.hypot(bx - ax, by - ay) < 0.01) {
            return;
          }

          // Collinearity tolerance — two walls count as "in a straight
          // line" when their shared axis coordinate differs by up to this
          // many metres. Loosened to 5 cm so walls that were drawn or
          // imported slightly off-axis still merge into one instead of
          // leaving a third bridge segment. The merged wall is snapped to
          // wallA's axis (see below) so it comes out perfectly straight.
          const AXIS_TOL = 0.05;
          const walls = floor.beams || [];

          // Does wall w have an endpoint at (px,py)? Return the OTHER end.
          const otherEndAt = (w, px, py) => {
            if (Math.hypot(w.x1 - px, w.y1 - py) < 0.05) return [w.x2, w.y2];
            if (Math.hypot(w.x2 - px, w.y2 - py) < 0.05) return [w.x1, w.y1];
            return null;
          };

          // Find the wall touching endpoint A and the wall touching B.
          let wIdxA = -1,
            wIdxB = -1,
            farA = null,
            farB = null;
          for (let i = 0; i < walls.length; i++) {
            if (wIdxA === -1) {
              const o = otherEndAt(walls[i], ax, ay);
              if (o) {
                wIdxA = i;
                farA = o;
                continue;
              }
            }
            if (wIdxB === -1) {
              const o = otherEndAt(walls[i], bx, by);
              if (o) {
                wIdxB = i;
                farB = o;
              }
            }
          }

          // Collinearity test: are the bridge and both walls on one
          // straight axis line (within AXIS_TOL)? Horizontal → all four y
          // within tol; vertical → all four x within tol.
          const isHoriz =
            Math.abs(ay - by) < AXIS_TOL &&
            wIdxA !== -1 &&
            Math.abs(farA[1] - ay) < AXIS_TOL &&
            wIdxB !== -1 &&
            Math.abs(farB[1] - by) < AXIS_TOL;
          const isVert =
            Math.abs(ax - bx) < AXIS_TOL &&
            wIdxA !== -1 &&
            Math.abs(farA[0] - ax) < AXIS_TOL &&
            wIdxB !== -1 &&
            Math.abs(farB[0] - bx) < AXIS_TOL;

          if (
            wIdxA !== -1 &&
            wIdxB !== -1 &&
            wIdxA !== wIdxB &&
            (isHoriz || isVert)
          ) {
            // ── MERGE: three collinear pieces become ONE wall ──
            // The merged wall spans from wallA's far end to wallB's far
            // end (the two extreme points along the shared axis).
            let p1, p2;
            if (isHoriz) {
              const xs = [farA[0], ax, bx, farB[0]];
              const y = ay;
              p1 = [Math.min(...xs), y];
              p2 = [Math.max(...xs), y];
            } else {
              const ys = [farA[1], ay, by, farB[1]];
              const x = ax;
              p1 = [x, Math.min(...ys)];
              p2 = [x, Math.max(...ys)];
            }
            // Keep wallA's name, drop wallB and the (never-created) bridge.
            const keepName = walls[wIdxA].name;
            const keepLabel = walls[wIdxA].label || "";
            const merged = {
              name: keepName,
              label: keepLabel,
              x1: p1[0],
              y1: p1[1],
              x2: p2[0],
              y2: p2[1],
            };
            const nextWalls = walls
              .filter((_, i) => i !== wIdxA && i !== wIdxB)
              .concat(merged);
            commitFloor({ ...floor, beams: nextWalls });
            resetToolState();
            return;
          }

          // ── Fallback: not collinear (an L-corner, or an endpoint not on
          // an existing wall) → add a bridge wall as before. ──
          const nextIdx = walls.length + 1;
          const wall = {
            name: `beam_${nextIdx}`,
            label: "",
            x1: ax,
            y1: ay,
            x2: bx,
            y2: by,
          };
          commitFloor({ ...floor, beams: [...walls, wall] });
          resetToolState();
          return;
        }
        setDrawing({
          ...drawing,
          x2: snapX,
          y2: snapY,
          wallPhase: "done",
        });
      } else {
        // "done" phase → clicking canvas restarts the endpoints
        setDrawing({
          ...drawing,
          x1: snapX,
          y1: snapY,
          x2: snapX,
          y2: snapY,
          wallPhase: "p2",
        });
      }
      return;
    }

    // If the click landed inside an already-committed room, custom, OR
    // shaft AND we're NOT actively drawing something new, open that
    // element for editing. Skip this if we're already editing.
    if (
      editIndex === null &&
      (!drawing ||
        (drawing.width === 0 &&
          !drawing.isCustom &&
          !drawing.isShaft &&
          !drawing.isStairs &&
          !drawing.isBeam &&
          !drawing.isColumn))
    ) {
      const hitRoom = floor.rooms.findIndex(
        (r) => x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height
      );
      if (hitRoom !== -1) {
        handleEditElement(hitRoom, "room");
        return;
      }
      const hitCustom = (floor.customs || []).findIndex((c) =>
        pointInPolygon(x, y, c.points || [])
      );
      if (hitCustom !== -1) {
        handleEditElement(hitCustom, "custom");
        return;
      }
      const hitShaft = (floor.shafts || []).findIndex((s) => {
        const der = shaftDerived(s);
        return (
          x >= s.x && x <= s.x + der.blockW && y >= s.y && y <= s.y + der.blockH
        );
      });
      if (hitShaft !== -1) {
        handleEditElement(hitShaft, "shaft");
        return;
      }
      const hitStairs = (floor.stairs || []).findIndex((s) => {
        const der = stairsDerived(s);
        return (
          x >= s.x && x <= s.x + der.blockW && y >= s.y && y <= s.y + der.blockH
        );
      });
      if (hitStairs !== -1) {
        handleEditElement(hitStairs, "stairs");
        return;
      }
      const hitColumn = (floor.columns || []).findIndex((c) =>
        pointInColumn(x, y, c, 0.15)
      );
      if (hitColumn !== -1) {
        handleEditElement(hitColumn, "column");
        return;
      }
      const hitWall = (floor.beams || []).findIndex((w) => {
        // Hit if the click is within 0.3m of the wall segment.
        const dx = w.x2 - w.x1,
          dy = w.y2 - w.y1;
        const L2 = dx * dx + dy * dy;
        if (L2 < 1e-6) return false;
        let t = ((x - w.x1) * dx + (y - w.y1) * dy) / L2;
        if (t < 0) t = 0;
        if (t > 1) t = 1;
        const px = w.x1 + t * dx,
          py = w.y1 + t * dy;
        return Math.hypot(x - px, y - py) <= 0.3;
      });
      if (hitWall !== -1) {
        handleEditElement(hitWall, "beam");
        return;
      }
    }

    // Otherwise it's rect corner placement.
    if (!drawing) return;
    if (drawing.isCustom) return; // custom uses its own click path above
    if (drawing.isShaft) return; // shaft uses its own click path above
    if (drawing.isStairs) return; // stairs uses its own click path above
    if (drawing.isBeam) return; // wall uses its own click path above
    if (drawing.isColumn) return; // column uses its own click path above
    if (clickMode === "corner") {
      // Single-click SW corner. If width/height are still zero, seed
      // sensible defaults so the outline appears immediately.
      setDrawing({
        ...drawing,
        x,
        y,
        width: drawing.width > 0 ? drawing.width : 3,
        height: drawing.height > 0 ? drawing.height : 3,
      });
    } else {
      // Two-corner mode.
      if (firstCorner == null) {
        setFirstCorner({ x, y });
        setDrawing({ ...drawing, x, y, width: 0, height: 0 });
      } else {
        const sx = Math.min(firstCorner.x, x);
        const sy = Math.min(firstCorner.y, y);
        // Enforce minimum: 0.6 m for balconies, 1 m for bathrooms, else 2 m.
        // (Balconies' strip rule — short≥0.6, long≥1 — is enforced by canAdd,
        //  not by the drag itself, so the user can drag a nearly-square then
        //  extend one side later.)
        const minS =
          drawing.type === "BALCONY"
            ? 0.6
            : drawing.type === "BATHROOM"
              ? 1
              : 2;
        const w = Math.max(minS, Math.abs(x - firstCorner.x));
        const h = Math.max(minS, Math.abs(y - firstCorner.y));
        setDrawing({ ...drawing, x: sx, y: sy, width: w, height: h });
        setFirstCorner(null);
      }
    }
  };

  const dispatchOpeningClick = (x, y) => {
    if (!drawing) return;

    // ─── STAIRS WALL CLICK — treat as a virtual rect ───────────────
    if (drawing.isStairs) {
      const der = stairsDerived(drawing);
      const vroom = {
        x: drawing.x,
        y: drawing.y,
        width: der.blockW,
        height: der.blockH,
      };
      const snap = computeSnapWithRoom(vroom, x, y);
      let wall, pos;
      if (snap && openingMode !== "open_door") {
        wall = snap.wall;
        pos = snap.snappedPos;
      } else {
        wall = detectRectWall(vroom, x, y);
        if (!wall) {
          alert("Click on one of the stairs' walls.");
          return;
        }
        pos = distAlongRectWall(vroom, wall, x, y);
      }
      if (openingMode === "door") {
        setOpeningDraft({
          kind: "door",
          wall,
          pos,
          width: 1.1,
          rotation: null,
          swing: null,
          hinge_side: null,
          is_main: true, // stairs default main-doors
        });
      } else if (openingMode === "open_door") {
        if (!openingDraft || openingDraft.pos1 == null) {
          setOpeningDraft({ kind: "open_door", wall, pos1: pos, pos2: null });
        } else if (openingDraft.wall !== wall) {
          alert("Both points must be on the same wall.");
        } else {
          setOpeningDraft({ ...openingDraft, pos2: pos });
        }
      }
      return;
    }

    // ─── SHAFT WALL CLICK — treat as a virtual rect ────────────────
    if (drawing.isShaft) {
      const der = shaftDerived(drawing);
      // Build a virtual room for wall detection with the computed size.
      const vroom = {
        x: drawing.x,
        y: drawing.y,
        width: der.blockW,
        height: der.blockH,
      };
      // Snap works on the virtual rect too.
      const snap = computeSnapWithRoom(vroom, x, y);
      let wall, pos;
      if (snap && openingMode !== "open_door") {
        wall = snap.wall;
        pos = snap.snappedPos;
      } else {
        wall = detectRectWall(vroom, x, y);
        if (!wall) {
          alert("Click on one of the shaft's walls.");
          return;
        }
        pos = distAlongRectWall(vroom, wall, x, y);
      }

      if (openingMode === "door") {
        setOpeningDraft({
          kind: "door",
          wall,
          pos,
          width: 1.1,
          rotation: null,
          swing: null,
          hinge_side: null,
          is_main: true, // shafts default main-doors
        });
      } else if (openingMode === "window") {
        setOpeningDraft({ kind: "window", wall, pos, width: 1.4 });
      } else if (openingMode === "open_door") {
        if (!openingDraft || openingDraft.pos1 == null) {
          setOpeningDraft({ kind: "open_door", wall, pos1: pos, pos2: null });
        } else if (openingDraft.wall !== wall) {
          alert("Both points must be on the same wall.");
        } else {
          setOpeningDraft({ ...openingDraft, pos2: pos });
        }
      }
      return;
    }

    // ─── CUSTOM POLYGON EDGE CLICK ─────────────────────────────────
    if (drawing.isCustom) {
      if (!drawing.points || drawing.points.length < 2) return;
      // For customs the snap system also works — computeSnap returns
      // { snappedX, snappedY, snappedPos, wall = "edgeN", edgeInfo }.
      const snap = computeSnap(x, y);
      let edgeInfo, along;
      if (snap && openingMode !== "open_door") {
        edgeInfo = snap.edgeInfo;
        along = snap.snappedPos;
      } else {
        const hit = detectCustomEdge(
          drawing.points,
          !!drawing.closed,
          x,
          y,
          0.35
        );
        if (!hit) {
          alert("Click on one of the polygon's edges.");
          return;
        }
        edgeInfo = hit;
        along = hit.along;
      }
      const wall = edgeInfo.edgeKey; // "edgeN"

      if (openingMode === "door") {
        setOpeningDraft({
          kind: "door",
          wall,
          pos: along,
          width: 1.1,
          rotation: null,
          swing: null,
          hinge_side: null,
          is_main: false,
          edgeInfo, // stashed so the form/canvas can render the marker
        });
      } else if (openingMode === "window") {
        setOpeningDraft({
          kind: "window",
          wall,
          pos: along,
          width: 1.4,
          edgeInfo,
        });
      } else if (openingMode === "open_door") {
        if (!openingDraft || openingDraft.pos1 == null) {
          setOpeningDraft({
            kind: "open_door",
            wall,
            pos1: along,
            pos2: null,
            edgeInfo,
          });
        } else if (openingDraft.wall !== wall) {
          alert("Both points must be on the same edge.");
        } else {
          setOpeningDraft({ ...openingDraft, pos2: along });
        }
      }
      return;
    }

    // ─── RECT ROOM WALL CLICK (existing path) ──────────────────────
    if (drawing.width <= 0 || drawing.height <= 0) return;
    // Auto-snap to the nearest snap point if the click is within 0.4 m.
    // For doors: snap to 0.2 m from either end (natural hinge positions).
    // For windows: snap to the wall's centre.
    // Open doors: no snap (each of the two clicks is independent).
    const snap = computeSnap(x, y);
    let wall, pos;
    if (snap && openingMode !== "open_door") {
      wall = snap.wall;
      pos = snap.snappedPos;
    } else {
      wall = detectRectWall(drawing, x, y);
      if (!wall) {
        alert("Click on one of the room's walls.");
        return;
      }
      pos = distAlongRectWall(drawing, wall, x, y);
    }

    // Bathrooms need narrower default openings.
    const isBathroom = drawing?.type === "BATHROOM";
    if (openingMode === "door") {
      // Default sensible values; user tweaks in the form.
      setOpeningDraft({
        kind: "door",
        wall,
        pos,
        width: isBathroom ? 0.75 : 1.1,
        rotation: null, // user picks
        swing: null,
        hinge_side: null,
        is_main: false,
      });
    } else if (openingMode === "window") {
      setOpeningDraft({
        kind: "window",
        wall,
        pos,
        width: isBathroom ? 0.35 : 1.4,
      });
    } else if (openingMode === "open_door") {
      // First or second click?
      if (!openingDraft || openingDraft.pos1 == null) {
        setOpeningDraft({ kind: "open_door", wall, pos1: pos, pos2: null });
      } else if (openingDraft.wall !== wall) {
        alert("Both points must be on the same wall.");
      } else {
        setOpeningDraft({ ...openingDraft, pos2: pos });
      }
    }
  };

  // ── Cursor tracking for live preview of opening placement ─────────
  const [cursor, setCursor] = useState(null);

  // Stage 4m: compute a hover-tooltip payload for the canvas. Returns
  // { text, x, y } in world coords when the cursor is over an element
  // AND no tool is active. Cursor stays as an arrow (see PlanCanvas).
  const hoverInfo = React.useMemo(() => {
    if (!cursor || selectedType) return null;
    const { x, y } = cursor;
    const round = (v) => Math.round(v * 100) / 100;
    // Rooms — bbox contains cursor
    for (const r of floor.rooms || []) {
      if (x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height) {
        return {
          text: `${r.label || r.name} (${r.type}) ${round(r.width)}×${round(r.height)}m`,
          x,
          y,
        };
      }
    }
    // Customs — polygon contains cursor (inside test)
    for (const c of floor.customs || []) {
      const pts = c.points || [];
      if (pts.length < 3) continue;
      let inside = false;
      for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        const [xi, yi] = pts[i],
          [xj, yj] = pts[j];
        if (
          yi > y !== yj > y &&
          x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi
        ) {
          inside = !inside;
        }
      }
      if (inside)
        return {
          text: `${c.label || c.name} (CUSTOM) ${pts.length} points`,
          x,
          y,
        };
    }
    // Shafts — same bbox test
    for (const s of floor.shafts || []) {
      const sw =
        s.width_dir === "x"
          ? s.step_width * 2 + s.separation
          : s.step_width * 2 + s.separation;
      // shaft's bbox depends on width_dir — approximate by depth * steps
      const total = s.steps * s.depth + (s.flat || 0);
      const w =
        s.width_dir === "y" ? s.step_width * 2 + (s.separation || 0) : total;
      const h =
        s.width_dir === "y" ? total : s.step_width * 2 + (s.separation || 0);
      if (x >= s.x && x <= s.x + w && y >= s.y && y <= s.y + h) {
        return {
          text: `${s.label || s.name} (SHAFT) ${round(w)}×${round(h)}m ${s.steps}st`,
          x,
          y,
        };
      }
    }
    // Stairs
    for (const s of floor.stairs || []) {
      const w = s.width_dir === "y" ? s.width : s.steps * s.depth;
      const h = s.width_dir === "y" ? s.steps * s.depth : s.width;
      if (x >= s.x && x <= s.x + w && y >= s.y && y <= s.y + h) {
        return {
          text: `${s.label || s.name} (STAIRS) ${s.steps}st ${round(w)}×${round(h)}m`,
          x,
          y,
        };
      }
    }
    // Walls — perpendicular distance
    for (const w of floor.beams || []) {
      const dx = w.x2 - w.x1,
        dy = w.y2 - w.y1;
      const L2 = dx * dx + dy * dy;
      if (L2 < 1e-9) continue;
      let u = ((x - w.x1) * dx + (y - w.y1) * dy) / L2;
      if (u < 0 || u > 1) continue;
      const px = w.x1 + u * dx,
        py = w.y1 + u * dy;
      if (Math.hypot(x - px, y - py) <= 0.15) {
        const L = Math.hypot(dx, dy);
        return {
          text: `${w.label || w.name} (WALL) ${round(L)}m`,
          x,
          y,
        };
      }
    }
    return null;
  }, [cursor, selectedType, floor]);

  // Compute snap points along a wall for the current opening kind.
  //   window   → single snap at the wall's centre
  //   door     → two snaps: 0.2 m from each end (natural hinge positions)
  //   open_door → no snap (needs two independent clicks)
  const wallSnapPositions = (wall, room, kind) => {
    if (!wall || !room || !kind) return [];
    const len = ["south", "north"].includes(wall) ? room.width : room.height;
    if (kind === "window") return [len / 2];
    if (kind === "door")
      return [0.2, len - 0.2].filter((p) => p > 0 && p < len);
    return [];
  };

  // Given a mouse world position, decide if it should snap.
  // Returns { snappedX, snappedY, snappedPos, wall, edgeInfo? } when snapping,
  // else null. Threshold = 0.4 m from a snap point (per user request).
  const computeSnap = (mx, my) => {
    if (!openingMode || !drawing) return null;

    // ── Custom polygon: snap along the nearest edge ─────────────────
    if (drawing.isCustom) {
      if (!drawing.points || drawing.points.length < 2) return null;
      const hit = detectCustomEdge(
        drawing.points,
        !!drawing.closed,
        mx,
        my,
        0.35
      );
      if (!hit) return null;
      const L = hit.edgeLen;
      const snapAlong =
        openingMode === "window"
          ? [L / 2]
          : openingMode === "door"
            ? [0.2, L - 0.2].filter((p) => p > 0 && p < L)
            : [];
      if (snapAlong.length === 0) return null;
      let best = null;
      const THRESH = 0.4;
      for (const p of snapAlong) {
        const d = Math.abs(hit.along - p);
        if (d <= THRESH && (best === null || d < best.d)) {
          best = { d, along: p };
        }
      }
      if (!best) return null;
      const [ax, ay] = hit.edgeStart;
      const [bx, by] = hit.edgeEnd;
      const ux = (bx - ax) / L,
        uy = (by - ay) / L;
      return {
        snappedX: ax + ux * best.along,
        snappedY: ay + uy * best.along,
        snappedPos: best.along,
        wall: hit.edgeKey,
        edgeInfo: hit,
      };
    }

    // ── Rectangle rooms (unchanged) ─────────────────────────────────
    if (drawing.isShaft) {
      // Route shaft snap through the virtual-rect helper below.
      const der = shaftDerived(drawing);
      const vroom = {
        x: drawing.x,
        y: drawing.y,
        width: der.blockW,
        height: der.blockH,
      };
      return computeSnapWithRoom(vroom, mx, my);
    }
    if (drawing.isStairs) {
      const der = stairsDerived(drawing);
      const vroom = {
        x: drawing.x,
        y: drawing.y,
        width: der.blockW,
        height: der.blockH,
      };
      return computeSnapWithRoom(vroom, mx, my);
    }
    if (drawing.width <= 0 || drawing.height <= 0) return null;
    return computeSnapWithRoom(drawing, mx, my);
  };

  // Reusable core: for any rectangle-shaped room (real rect or virtual
  // shaft rect), return the snap point near mouse (mx,my) for the current
  // openingMode, or null.
  const computeSnapWithRoom = (room, mx, my) => {
    if (!openingMode) return null;
    if (!room || room.width <= 0 || room.height <= 0) return null;
    const wall = detectRectWall(room, mx, my);
    if (!wall) return null;
    const snapAlong = wallSnapPositions(wall, room, openingMode);
    if (snapAlong.length === 0) return null;
    const rawAlong = distAlongRectWall(room, wall, mx, my);
    let best = null;
    const THRESH = 0.4;
    for (const p of snapAlong) {
      const d = Math.abs(rawAlong - p);
      if (d <= THRESH && (best === null || d < best.d)) {
        best = { d, snapAlong: p };
      }
    }
    if (!best) return null;
    let snappedX = mx,
      snappedY = my;
    if (wall === "south") {
      snappedX = room.x + best.snapAlong;
      snappedY = room.y;
    }
    if (wall === "north") {
      snappedX = room.x + best.snapAlong;
      snappedY = room.y + room.height;
    }
    if (wall === "west") {
      snappedX = room.x;
      snappedY = room.y + best.snapAlong;
    }
    if (wall === "east") {
      snappedX = room.x + room.width;
      snappedY = room.y + best.snapAlong;
    }
    return { snappedX, snappedY, snappedPos: best.snapAlong, wall };
  };

  // previewOpening carries either just a mouse dot (no snap) or a snap
  // marker with kind + wall so the canvas can render an outlined circle.
  const previewOpening = useMemo(() => {
    if (!openingMode || !drawing || !cursor) return null;

    // Custom polygon path
    if (drawing.isCustom) {
      if (!drawing.points || drawing.points.length < 2) return null;
      const hit = detectCustomEdge(
        drawing.points,
        !!drawing.closed,
        cursor.x,
        cursor.y,
        0.35
      );
      if (!hit) return null;
      const snap = computeSnap(cursor.x, cursor.y);
      if (snap) {
        return {
          x: snap.snappedX,
          y: snap.snappedY,
          isSnap: true,
          kind: openingMode,
        };
      }
      // Cursor near edge but not snapping → show the mouse projected
      // onto the edge as a plain grey circle.
      return {
        x: hit.edgeStart[0] + (hit.edgeEnd[0] - hit.edgeStart[0]) * hit.t,
        y: hit.edgeStart[1] + (hit.edgeEnd[1] - hit.edgeStart[1]) * hit.t,
        isSnap: false,
        kind: openingMode,
      };
    }

    // Shaft path — same wall/edge detection as rects, but on the
    // computed rectangle.
    if (drawing.isShaft) {
      const der = shaftDerived(drawing);
      if (der.blockW <= 0 || der.blockH <= 0) return null;
      const vroom = {
        x: drawing.x,
        y: drawing.y,
        width: der.blockW,
        height: der.blockH,
      };
      const wall = detectRectWall(vroom, cursor.x, cursor.y);
      if (!wall) return null;
      const snap = computeSnapWithRoom(vroom, cursor.x, cursor.y);
      if (snap) {
        return {
          x: snap.snappedX,
          y: snap.snappedY,
          isSnap: true,
          kind: openingMode,
        };
      }
      return { x: cursor.x, y: cursor.y, isSnap: false, kind: openingMode };
    }

    // Stairs path — same virtual-rect approach as shaft
    if (drawing.isStairs) {
      const der = stairsDerived(drawing);
      if (der.blockW <= 0 || der.blockH <= 0) return null;
      const vroom = {
        x: drawing.x,
        y: drawing.y,
        width: der.blockW,
        height: der.blockH,
      };
      const wall = detectRectWall(vroom, cursor.x, cursor.y);
      if (!wall) return null;
      const snap = computeSnapWithRoom(vroom, cursor.x, cursor.y);
      if (snap) {
        return {
          x: snap.snappedX,
          y: snap.snappedY,
          isSnap: true,
          kind: openingMode,
        };
      }
      return { x: cursor.x, y: cursor.y, isSnap: false, kind: openingMode };
    }

    // Rect path (unchanged)
    if (drawing.width <= 0 || drawing.height <= 0) return null;
    const wall = detectRectWall(drawing, cursor.x, cursor.y);
    if (!wall) return null;
    const snap = computeSnap(cursor.x, cursor.y);
    if (snap) {
      return {
        x: snap.snappedX,
        y: snap.snappedY,
        isSnap: true,
        kind: openingMode,
      };
    }
    return { x: cursor.x, y: cursor.y, isSnap: false, kind: openingMode };
  }, [openingMode, drawing, cursor]);

  // ── Commit / discard ──────────────────────────────────────────────
  const handleCommitElement = () => {
    if (!drawing) return;

    // ── SHAFT commit ─────────────────────────────────────────────
    if (drawing.isShaft) {
      // Ground truth from models.py line 266-280: StairShaft has these
      // init fields:
      //   name, label, x, y, steps, step_width, width_dir, depth,
      //   separation, flat, up_dir, doors, windows, open_doors, elevators
      // The frontend's `rotation` field is UI-only (converts to up_dir).
      const der = shaftDerived(drawing);
      // Doors on shafts default to is_main=true but user can uncheck.
      const cleanDoors = (drawing.doors || []).map((dr) => ({
        wall: dr.wall,
        position: dr.position,
        width: dr.width,
        swing: dr.swing || "right",
        hinge_side: dr.hinge_side || "start",
        is_main: !!dr.is_main,
        connects_to: "",
      }));
      const shaft = {
        name: drawing.name,
        label: drawing.label || drawing.name,
        x: drawing.x,
        y: drawing.y,
        steps: der.steps,
        step_width: der.stepWidth,
        width_dir: der.widthDir,
        depth: der.depth,
        separation: der.separation,
        flat: der.flat,
        up_dir: der.upDir,
        doors: cleanDoors,
        windows: drawing.windows || [],
        open_doors: drawing.open_doors || [],
        elevators: drawing.elevators || [],
      };
      if (editIndex !== null && editKind === "shaft") {
        const nextShafts = [...(floor.shafts || [])];
        nextShafts[editIndex] = shaft;
        commitFloor({ ...floor, shafts: nextShafts });
        setEditIndex(null);
        setEditKind(null);
      } else {
        commitFloor({ ...floor, shafts: [...(floor.shafts || []), shaft] });
      }
      resetToolState();
      return;
    }

    // ── WALL commit ──────────────────────────────────────────────
    if (drawing.isBeam) {
      // Wall schema: name, label, x1, y1, x2, y2 (models.py line 317).
      // CLOSE_OPENING auto-names the wall so no form is needed.
      let beamName = drawing.name;
      if (drawing.isCloseOpening && !beamName) {
        const nextIdx = (floor.beams || []).length + 1;
        beamName = `beam_${nextIdx}`;
      }
      const wall = {
        name: beamName,
        label: drawing.label || "",
        x1: drawing.x1,
        y1: drawing.y1,
        x2: drawing.x2,
        y2: drawing.y2,
      };
      if (editIndex !== null && editKind === "beam") {
        const nextWalls = [...(floor.beams || [])];
        nextWalls[editIndex] = wall;
        commitFloor({ ...floor, beams: nextWalls });
        setEditIndex(null);
        setEditKind(null);
      } else {
        commitFloor({ ...floor, beams: [...(floor.beams || []), wall] });
      }
      resetToolState();
      return;
    }

    // ── COLUMN commit ────────────────────────────────────────────
    if (drawing.isColumn) {
      const column = {
        name: drawing.name,
        label: drawing.label || "",
        x: drawing.x,
        y: drawing.y,
        thickness: drawing.thickness ?? 0.2,
        width: drawing.width ?? 0.4,
        direction: drawing.direction ?? "x",
        angle: drawing.direction === "angled" ? (drawing.angle ?? 0) : 0,
      };
      if (editIndex !== null && editKind === "column") {
        const nextCols = [...(floor.columns || [])];
        nextCols[editIndex] = column;
        commitFloor({ ...floor, columns: nextCols });
        setEditIndex(null);
        setEditKind(null);
      } else {
        commitFloor({ ...floor, columns: [...(floor.columns || []), column] });
      }
      resetToolState();
      return;
    }

    // ── STAIRS commit ────────────────────────────────────────────
    if (drawing.isStairs) {
      // Ground truth from models.py line 173-200: Stairs has these
      // init fields: name, label, x, y, steps, width, width_dir,
      // depth, up_dir, doors, open_doors.  (No windows, no elevators.)
      const der = stairsDerived(drawing);
      const cleanDoors = (drawing.doors || []).map((dr) => ({
        wall: dr.wall,
        position: dr.position,
        width: dr.width,
        swing: dr.swing || "right",
        hinge_side: dr.hinge_side || "start",
        is_main: !!dr.is_main,
        connects_to: "",
      }));
      const stairs = {
        name: drawing.name,
        label: drawing.label || drawing.name,
        x: drawing.x,
        y: drawing.y,
        steps: der.steps,
        width: der.width,
        width_dir: der.widthDir,
        depth: der.depth,
        up_dir: der.upDir,
        doors: cleanDoors,
        open_doors: drawing.open_doors || [],
      };
      if (editIndex !== null && editKind === "stairs") {
        const nextStairs = [...(floor.stairs || [])];
        nextStairs[editIndex] = stairs;
        commitFloor({ ...floor, stairs: nextStairs });
        setEditIndex(null);
        setEditKind(null);
      } else {
        commitFloor({ ...floor, stairs: [...(floor.stairs || []), stairs] });
      }
      resetToolState();
      return;
    }

    // ── CUSTOM polygon commit ─────────────────────────────────────
    if (drawing.isCustom) {
      // Ground truth: CustomRoom in models.py has ONLY these init fields:
      //   name, label, points, doors, windows, open_doors, closed
      // (center_x and center_y are @property in Python — computed, not
      //  stored.  There is NO 'type' field on CustomRoom.)
      const custom = {
        name: drawing.name,
        label: drawing.label || drawing.name,
        points: (drawing.points || []).map(([x, y]) => [x, y]),
        closed: !!drawing.closed,
        doors: drawing.doors || [],
        windows: drawing.windows || [],
        open_doors: drawing.open_doors || [],
      };
      if (editIndex !== null && editKind === "custom") {
        const nextCustoms = [...(floor.customs || [])];
        nextCustoms[editIndex] = custom;
        commitFloor({ ...floor, customs: nextCustoms });
        setEditIndex(null);
        setEditKind(null);
      } else {
        commitFloor({
          ...floor,
          customs: [...(floor.customs || []), custom],
        });
      }
      // Reset for another custom of the same subtype.
      resetToolState();
      return;
    }

    // ── Rectangle room commit (unchanged) ─────────────────────────
    // Room doesn't take connects_to (that's a Door field).
    // Also strip our "rotation" UI field from doors — the Python schema
    // only knows: wall, position, width, swing, hinge_side, is_main.
    const cleanDoors = (drawing.doors || []).map((d) => ({
      wall: d.wall,
      position: d.position,
      width: d.width,
      swing: d.swing || "right",
      hinge_side: d.hinge_side || "start",
      is_main: !!d.is_main,
      connects_to: "",
    }));
    const room = {
      name: drawing.name,
      label: drawing.label || drawing.name,
      type: drawing.type,
      x: drawing.x,
      y: drawing.y,
      width: drawing.width,
      height: drawing.height,
      doors: cleanDoors,
      windows: drawing.windows || [],
      open_doors: drawing.open_doors || [],
    };
    if (editIndex !== null && editKind !== "custom") {
      // Replace the room at editIndex, keep it at the same list position.
      const nextRooms = [...floor.rooms];
      nextRooms[editIndex] = room;
      commitFloor({ ...floor, rooms: nextRooms });
      setEditIndex(null);
      setEditKind(null);
    } else {
      // Fresh add — append to the end.
      commitFloor({ ...floor, rooms: [...floor.rooms, room] });
    }
    // Stage 3r: auto-deselect after commit — matches AutoCAD.
    resetToolState();
  };

  const handleDiscardInProgress = () => {
    setDrawing(selectedType ? makeEmptyDrawing(selectedType) : null);
    setFirstCorner(null);
    setOpeningMode(null);
    setOpeningDraft(null);
    setEditIndex(null);
  };

  // ─── Text label deletion (Stage 3z Issue 1) ────────────────────────
  // Delete a text label by index. Goes through commitFloor so Ctrl+Z
  // restores it. Only used from the ElementsList text section.
  const handleDeleteText = (idx) => {
    commitFloor({
      ...floor,
      texts: (floor.texts || []).filter((_, i) => i !== idx),
    });
  };

  // Stage 3z.1: bulk-delete all "orphan" text labels — labels whose
  // position is NOT inside any room / custom / shaft / stairs bbox AND
  // is more than 0.5m from any wall. Useful for cleaning up labels left
  // behind after their surrounding geometry was deleted. Single undo.
  const handleDeleteOrphanTexts = () => {
    const texts = floor.texts || [];
    if (texts.length === 0) return;
    const isOrphan = (t) => {
      // inside a room rect?
      for (const r of floor.rooms || []) {
        if (
          t.x >= r.x &&
          t.x <= r.x + r.width &&
          t.y >= r.y &&
          t.y <= r.y + r.height
        )
          return false;
      }
      // inside a custom polygon?
      for (const c of floor.customs || []) {
        if (pointInPolygon(t.x, t.y, c.points || [])) return false;
      }
      // inside a shaft or stairs bbox?
      for (const s of floor.shafts || []) {
        const der = shaftDerived(s);
        if (
          t.x >= s.x &&
          t.x <= s.x + der.blockW &&
          t.y >= s.y &&
          t.y <= s.y + der.blockH
        )
          return false;
      }
      for (const s of floor.stairs || []) {
        const der = stairsDerived(s);
        if (
          t.x >= s.x &&
          t.x <= s.x + der.blockW &&
          t.y >= s.y &&
          t.y <= s.y + der.blockH
        )
          return false;
      }
      // near a wall segment (within 0.5m)?
      for (const w of floor.beams || []) {
        const dx = w.x2 - w.x1,
          dy = w.y2 - w.y1;
        const L2 = dx * dx + dy * dy;
        if (L2 < 1e-6) continue;
        let u = ((t.x - w.x1) * dx + (t.y - w.y1) * dy) / L2;
        if (u < 0) u = 0;
        if (u > 1) u = 1;
        const px = w.x1 + u * dx,
          py = w.y1 + u * dy;
        if (Math.hypot(t.x - px, t.y - py) <= 0.5) return false;
      }
      return true;
    };
    const kept = texts.filter((t) => !isOrphan(t));
    const removed = texts.length - kept.length;
    if (removed === 0) return;
    commitFloor({ ...floor, texts: kept });
  };

  const handleDeleteElement = (idx, kind = "room") => {
    // If we're editing THIS same element, cancel the edit AND clear
    // the tool so we don't stay in "drawing wall" mode after Delete-key.
    if (editIndex === idx && editKind === kind) {
      setEditIndex(null);
      setEditKind(null);
      resetToolState();
    } else if (editIndex !== null && editKind === kind && idx < editIndex) {
      // The list shifted; keep our edit pointer valid.
      setEditIndex(editIndex - 1);
    }
    if (kind === "custom") {
      commitFloor({
        ...floor,
        customs: (floor.customs || []).filter((_, i) => i !== idx),
      });
    } else if (kind === "shaft") {
      commitFloor({
        ...floor,
        shafts: (floor.shafts || []).filter((_, i) => i !== idx),
      });
    } else if (kind === "stairs") {
      commitFloor({
        ...floor,
        stairs: (floor.stairs || []).filter((_, i) => i !== idx),
      });
    } else if (kind === "beam") {
      commitFloor({
        ...floor,
        beams: (floor.beams || []).filter((_, i) => i !== idx),
      });
    } else if (kind === "column") {
      commitFloor({
        ...floor,
        columns: (floor.columns || []).filter((_, i) => i !== idx),
      });
    } else if (kind === "slab") {
      commitFloor({
        ...floor,
        slabs: (floor.slabs || []).filter((_, i) => i !== idx),
      });
    } else {
      commitFloor({ ...floor, rooms: floor.rooms.filter((_, i) => i !== idx) });
    }
  };

  // Reopen an already-committed room OR custom OR shaft for editing.
  // Stays in the list (highlighted "editing…") so it renders on the
  // canvas until the user clicks "Save changes" or "Cancel".
  const handleEditElement = (idx, kind = "room") => {
    // Q3: entering edit mode clears any multi-selection.
    setSelection([]);
    if (kind === "beam") {
      const w = (floor.beams || [])[idx];
      if (!w) return;
      setSelectedType("BEAM");
      setDrawing({
        type: "BEAM",
        isBeam: true,
        name: w.name,
        label: w.label || "",
        x1: w.x1,
        y1: w.y1,
        x2: w.x2,
        y2: w.y2,
        wallPhase: "done", // both endpoints exist
      });
      setEditIndex(idx);
      setEditKind("beam");
      setFirstCorner(null);
      setOpeningMode(null);
      setOpeningDraft(null);
      return;
    }
    if (kind === "column") {
      const c = (floor.columns || [])[idx];
      if (!c) return;
      setSelectedType("COLUMN");
      setDrawing({
        type: "COLUMN",
        isColumn: true,
        name: c.name,
        label: c.label || "",
        x: c.x,
        y: c.y,
        thickness: c.thickness ?? 0.2,
        width: c.width ?? 0.4,
        direction: c.direction ?? "x",
        angle: c.angle ?? 0,
        columnPhase: "done",
      });
      setEditIndex(idx);
      setEditKind("column");
      setFirstCorner(null);
      setOpeningMode(null);
      setOpeningDraft(null);
      return;
    }
    if (kind === "stairs") {
      const s = (floor.stairs || [])[idx];
      if (!s) return;
      // Reverse-derive rotation from up_dir + width_dir.
      let rotation = "clockwise";
      if (s.width_dir === "x") {
        rotation = s.up_dir === "north" ? "clockwise" : "counterclockwise";
      } else {
        rotation = s.up_dir === "west" ? "clockwise" : "counterclockwise";
      }
      setSelectedType("STAIRS");
      setDrawing({
        type: "STAIRS",
        isStairs: true,
        name: s.name,
        label: s.label,
        x: s.x,
        y: s.y,
        steps: s.steps ?? 5,
        width: s.width ?? 1.2,
        width_dir: s.width_dir ?? "x",
        depth: s.depth ?? 0.3,
        rotation,
        doors: s.doors || [],
        open_doors: s.open_doors || [],
      });
      setEditIndex(idx);
      setEditKind("stairs");
      setFirstCorner(null);
      setOpeningMode(null);
      setOpeningDraft(null);
      return;
    }
    if (kind === "shaft") {
      const s = (floor.shafts || [])[idx];
      if (!s) return;
      // Reverse-derive rotation from up_dir + width_dir (matches the
      // main.py mapping at lines 489-492).
      let rotation = "clockwise";
      if (s.width_dir === "x") {
        rotation = s.up_dir === "north" ? "clockwise" : "counterclockwise";
      } else {
        rotation = s.up_dir === "west" ? "clockwise" : "counterclockwise";
      }
      setSelectedType("SHAFT");
      setDrawing({
        type: "SHAFT",
        isShaft: true,
        name: s.name,
        label: s.label,
        x: s.x,
        y: s.y,
        steps: s.steps ?? 22,
        step_width: s.step_width ?? 1.5,
        width_dir: s.width_dir ?? "x",
        depth: s.depth ?? 0.3,
        separation: s.separation ?? 0.3,
        flat: s.flat ?? 1.5,
        rotation,
        doors: s.doors || [],
        windows: s.windows || [],
        open_doors: s.open_doors || [],
        elevators: s.elevators || [],
      });
      setEditIndex(idx);
      setEditKind("shaft");
      setFirstCorner(null);
      setOpeningMode(null);
      setOpeningDraft(null);
      return;
    }
    if (kind === "custom") {
      const c = (floor.customs || [])[idx];
      if (!c) return;
      setSelectedType("CUSTOM");
      setDrawing({
        type: "CUSTOM",
        isCustom: true,
        // CustomRoom has no `type` field in Python — the subtype selector
        // is UI-only. Default to HALL when we don't know.
        subtype: "HALL",
        name: c.name,
        label: c.label,
        points: (c.points || []).map(([x, y]) => [x, y]),
        closed: !!c.closed,
        // A committed custom re-opens in the openings phase so the user
        // can tweak the points from the form OR click Back to editing
        // points.
        polygonPhase: "openings",
        doors: c.doors || [],
        windows: c.windows || [],
        open_doors: c.open_doors || [],
      });
      setEditIndex(idx);
      setEditKind("custom");
      setFirstCorner(null);
      setOpeningMode(null);
      setOpeningDraft(null);
      return;
    }
    // Rect room path
    const r = floor.rooms[idx];
    if (!r) return;
    setSelectedType(r.type);
    setDrawing({
      type: r.type,
      name: r.name,
      label: r.label,
      x: r.x,
      y: r.y,
      width: r.width,
      height: r.height,
      doors: r.doors || [],
      windows: r.windows || [],
      open_doors: r.open_doors || [],
    });
    setEditIndex(idx);
    setEditKind("room");
    setFirstCorner(null);
    setOpeningMode(null);
    setOpeningDraft(null);
  };

  // After committing (add or save-edit) OR cancelling an edit, snap back
  // to the "no active tool" state — matches AutoCAD, and prevents
  // accidentally starting a NEW element with the next canvas click.
  // Stage 3r: user asked for this UX.
  const resetToolState = () => {
    setSelectedType(null);
    setDrawing(null);
    setFirstCorner(null);
    setOpeningMode(null);
    setOpeningDraft(null);
    setDetectedRoomBbox(null);
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditKind(null);
    resetToolState();
  };

  // ── Element drag-handle updates (Stage 6a) ──────────────────────────
  // Called continuously by PlanCanvas while the user drags a handle on
  // the element currently open for edit (`drawing`). Merges the geometry
  // patch straight into `drawing` — since ElementForm's inputs are
  // already controlled from `drawing`, this makes the text fields track
  // the mouse live, with no extra wiring needed there.
  //
  // For ROOM-like types (everything except CUSTOM/SHAFT/STAIRS/WALL),
  // a width or height change also re-anchors existing doors/windows/
  // open_doors — the exact same rescaleOpenings() logic already used by
  // the form's own width/height number inputs, so dragging a resize
  // handle behaves identically to typing a new width/height by hand.
  const handleElementDrag = (patch) => {
    setDrawing((d) => {
      if (!d) return d;
      const isRoomLike = !d.isCustom && !d.isShaft && !d.isStairs && !d.isBeam;
      if (isRoomLike) {
        let merged = { ...d };
        if (patch.width !== undefined && patch.width !== d.width) {
          const r = rescaleOpenings(merged, "width", patch.width);
          merged = { ...merged, width: patch.width, ...r };
        }
        if (patch.height !== undefined && patch.height !== d.height) {
          const r = rescaleOpenings(merged, "height", patch.height);
          merged = { ...merged, height: patch.height, ...r };
        }
        if (patch.x !== undefined) merged.x = patch.x;
        if (patch.y !== undefined) merged.y = patch.y;
        return merged;
      }
      // NEW: custom polygons — rescale openings when a vertex drag
      // changes an edge's length, same reasoning as rooms above.
      if (d.isCustom && patch.points) {
        const r = rescaleCustomOpenings(d, d.points, patch.points);
        return { ...d, points: patch.points, ...r };
      }
      return { ...d, ...patch };
    });
  };

  // ═══════════════════════════════════════════════════════════════════
  //  MULTI-SELECTION GROUP MOVE / RESIZE (Stage 8d — gesture stacking)
  // ═══════════════════════════════════════════════════════════════════
  //
  // Earlier versions stored ONE pending change (either a move delta or
  // a resize delta) — starting a SECOND gesture of a different type
  // replaced the first entirely, silently discarding it. Fixed by
  // storing the FULL CURRENT geometry per selected room instead of a
  // single delta, and having every new gesture build on top of
  // whatever's already pending rather than on the original floor data.
  //
  //   groupPendingChange = {
  //     rooms: { [roomIndex]: {x,y,width,height,doors,windows,open_doors} },
  //     nonRoomDx, nonRoomDy,   // cumulative move offset for wall/custom/
  //                             // shaft/stairs kinds (resize never touches
  //                             // these, so a running total is enough)
  //     lastGesture: {type:'move',dx,dy} | {type:'resize',edge,delta},
  //   } | null
  //
  //   groupGestureBaseRef = the state BEFORE the gesture currently in
  //   progress — captured once at drag-start (mousedown) so every tick,
  //   and every typed edit, recomputes cleanly from the SAME starting
  //   point without re-reading (and re-losing) intermediate state.

  const groupGestureBaseRef = React.useRef(null);

  // A fresh base built straight from `floor` — used only when nothing
  // is pending yet (the very first gesture since Save/Cancel).
  const buildFreshGroupBase = () => {
    const rooms = {};
    for (const sel of selection) {
      if (sel.kind !== "room") continue;
      const r = floor.rooms?.[sel.index];
      if (!r) continue;
      rooms[sel.index] = {
        x: r.x,
        y: r.y,
        width: r.width,
        height: r.height,
        doors: r.doors,
        windows: r.windows,
        open_doors: r.open_doors,
      };
    }
    return { rooms, nonRoomDx: 0, nonRoomDy: 0 };
  };

  // The authoritative "current" state for a brand new gesture: whatever
  // is already pending (preserving every earlier gesture), or fresh
  // from floor if nothing is pending.
  const currentGroupBase = () => groupPendingChange || buildFreshGroupBase();

  // Stage 8c chaining logic (unchanged) — rooms sharing a wall along the
  // resize axis follow each other so shared walls don't overlap/gap.
  const chainedGroupResize = (rooms, edge, delta) => {
    const ADJACENCY_TOL = 0.02; // metres
    const out = new Map();

    if (edge === "E") {
      const sorted = [...rooms].sort((a, b) => a.x - b.x);
      let prevOrigEast = null,
        prevNewEast = null;
      for (const r of sorted) {
        const newWidth = Math.max(0.05, +(r.width + delta).toFixed(3));
        const chained =
          prevOrigEast !== null &&
          Math.abs(r.x - prevOrigEast) <= ADJACENCY_TOL;
        const newX = chained ? prevNewEast : r.x;
        out.set(r.index, { x: +newX.toFixed(3), width: newWidth });
        prevNewEast = +(newX + newWidth).toFixed(3);
        prevOrigEast = +(r.x + r.width).toFixed(3);
      }
    } else if (edge === "W") {
      const sorted = [...rooms].sort((a, b) => b.x - a.x);
      let prevOrigWest = null,
        prevNewWest = null;
      for (const r of sorted) {
        const newWidth = Math.max(0.05, +(r.width + delta).toFixed(3));
        const chained =
          prevOrigWest !== null &&
          Math.abs(r.x + r.width - prevOrigWest) <= ADJACENCY_TOL;
        const eastEdge = chained ? prevNewWest : r.x + r.width;
        const newX = +(eastEdge - newWidth).toFixed(3);
        out.set(r.index, { x: newX, width: newWidth });
        prevNewWest = newX;
        prevOrigWest = r.x;
      }
    } else if (edge === "N") {
      const sorted = [...rooms].sort((a, b) => a.y - b.y);
      let prevOrigNorth = null,
        prevNewNorth = null;
      for (const r of sorted) {
        const newHeight = Math.max(0.05, +(r.height + delta).toFixed(3));
        const chained =
          prevOrigNorth !== null &&
          Math.abs(r.y - prevOrigNorth) <= ADJACENCY_TOL;
        const newY = chained ? prevNewNorth : r.y;
        out.set(r.index, { y: +newY.toFixed(3), height: newHeight });
        prevNewNorth = +(newY + newHeight).toFixed(3);
        prevOrigNorth = +(r.y + r.height).toFixed(3);
      }
    } else {
      // "S"
      const sorted = [...rooms].sort((a, b) => b.y - a.y);
      let prevOrigSouth = null,
        prevNewSouth = null;
      for (const r of sorted) {
        const newHeight = Math.max(0.05, +(r.height + delta).toFixed(3));
        const chained =
          prevOrigSouth !== null &&
          Math.abs(r.y + r.height - prevOrigSouth) <= ADJACENCY_TOL;
        const northEdge = chained ? prevNewSouth : r.y + r.height;
        const newY = +(northEdge - newHeight).toFixed(3);
        out.set(r.index, { y: newY, height: newHeight });
        prevNewSouth = newY;
        prevOrigSouth = r.y;
      }
    }
    return out;
  };

  // Apply ONE gesture on top of a given base. Used both by the
  // continuous drag-tick handlers below AND by the pending bar's typed
  // edit fields — typing a value is just "redo this gesture with a
  // manually-entered amount instead of mouse movement," so both paths
  // share this single function to guarantee identical behaviour.
  const applyGestureToBase = (base, gesture) => {
    if (gesture.type === "move") {
      const { dx, dy } = gesture;
      const rooms = {};
      for (const [idx, r] of Object.entries(base.rooms)) {
        rooms[idx] = {
          ...r,
          x: +(r.x + dx).toFixed(3),
          y: +(r.y + dy).toFixed(3),
        };
      }
      return {
        rooms,
        nonRoomDx: +(base.nonRoomDx + dx).toFixed(3),
        nonRoomDy: +(base.nonRoomDy + dy).toFixed(3),
        lastGesture: gesture,
      };
    }
    // resize
    const { edge, delta } = gesture;
    const selRooms = Object.entries(base.rooms).map(([idx, r]) => ({
      index: idx,
      x: r.x,
      y: r.y,
      width: r.width,
      height: r.height,
    }));
    const chained = chainedGroupResize(selRooms, edge, delta);
    const isHeight = edge === "S" || edge === "N";
    const rooms = { ...base.rooms };
    for (const [idx, patch] of chained.entries()) {
      const r = base.rooms[idx];
      if (!r) continue;
      const openings = isHeight
        ? rescaleOpenings(r, "height", patch.height)
        : rescaleOpenings(r, "width", patch.width);
      rooms[idx] = { ...r, ...patch, ...openings };
    }
    return {
      rooms,
      nonRoomDx: base.nonRoomDx,
      nonRoomDy: base.nonRoomDy,
      lastGesture: gesture,
    };
  };

  // Called once when a NEW drag gesture STARTS (mousedown) — captures
  // the state BEFORE this gesture so every tick recomputes cleanly.
  const handleGroupDragStart = () => {
    groupGestureBaseRef.current = currentGroupBase();
  };
  const handleGroupResizeStart = () => {
    groupGestureBaseRef.current = currentGroupBase();
  };

  const handleGroupDragMove = (dx, dy) => {
    const base = groupGestureBaseRef.current || currentGroupBase();
    setGroupPendingChange(applyGestureToBase(base, { type: "move", dx, dy }));
  };
  const handleGroupDragEnd = (dx, dy) => {
    if (!dx && !dy) return; // no real movement — leave pending as-is
    handleGroupDragMove(dx, dy); // same computation, final tick
  };

  const handleGroupResizeMove = (edge, delta) => {
    const base = groupGestureBaseRef.current || currentGroupBase();
    setGroupPendingChange(
      applyGestureToBase(base, { type: "resize", edge, delta })
    );
  };
  const handleGroupResizeEnd = (edge, delta) => {
    if (!delta) return;
    handleGroupResizeMove(edge, delta);
  };

  const applyGroupPendingChange = (change) => {
    const next = {
      rooms: [...(floor.rooms || [])],
      beams: [...(floor.beams || [])],
      customs: [...(floor.customs || [])],
      shafts: [...(floor.shafts || [])],
      stairs: [...(floor.stairs || [])],
    };
    for (const [idxStr, patch] of Object.entries(change.rooms || {})) {
      const idx = +idxStr;
      if (!next.rooms[idx]) continue;
      next.rooms[idx] = { ...next.rooms[idx], ...patch };
    }
    const dx = change.nonRoomDx || 0,
      dy = change.nonRoomDy || 0;
    if (dx || dy) {
      for (const sel of selection) {
        if (sel.kind === "beam") {
          const w = next.beams[sel.index];
          if (!w) continue;
          next.beams[sel.index] = {
            ...w,
            x1: +(w.x1 + dx).toFixed(3),
            y1: +(w.y1 + dy).toFixed(3),
            x2: +(w.x2 + dx).toFixed(3),
            y2: +(w.y2 + dy).toFixed(3),
          };
        } else if (sel.kind === "custom") {
          const c = next.customs[sel.index];
          if (!c) continue;
          next.customs[sel.index] = {
            ...c,
            points: (c.points || []).map(([px, py]) => [
              +(px + dx).toFixed(3),
              +(py + dy).toFixed(3),
            ]),
          };
        } else if (sel.kind === "shaft") {
          const s = next.shafts[sel.index];
          if (!s) continue;
          next.shafts[sel.index] = {
            ...s,
            x: +(s.x + dx).toFixed(3),
            y: +(s.y + dy).toFixed(3),
          };
        } else if (sel.kind === "stairs") {
          const s = next.stairs[sel.index];
          if (!s) continue;
          next.stairs[sel.index] = {
            ...s,
            x: +(s.x + dx).toFixed(3),
            y: +(s.y + dy).toFixed(3),
          };
        }
      }
    }
    commitFloor({ ...floor, ...next });
  };

  const handleSaveGroupChange = () => {
    if (!groupPendingChange) return;
    applyGroupPendingChange(groupPendingChange);
    setGroupPendingChange(null);
    groupGestureBaseRef.current = null;
  };

  const handleCancelGroupChange = () => {
    setGroupPendingChange(null);
    groupGestureBaseRef.current = null;
  };

  // Bounding box (west/south corner) of the selection as it stands
  // BEFORE the gesture currently in progress — used to show/edit an
  // absolute "x = / y =" position for a pending move, same idea as
  // Copy/Paste's reference point. Reads the gesture BASE (not floor
  // directly), so it correctly reflects any earlier resize/move
  // already stacked into the pending state.
  const groupSelectionOriginBBox = () => {
    const base = groupGestureBaseRef.current || currentGroupBase();
    let minX = Infinity,
      minY = Infinity;
    for (const sel of selection) {
      if (sel.kind === "room") {
        const r = base.rooms[sel.index] || floor.rooms?.[sel.index];
        if (!r) continue;
        minX = Math.min(minX, r.x);
        minY = Math.min(minY, r.y);
      } else if (sel.kind === "beam") {
        const w = floor.beams?.[sel.index];
        if (!w) continue;
        minX = Math.min(minX, w.x1 + base.nonRoomDx, w.x2 + base.nonRoomDx);
        minY = Math.min(minY, w.y1 + base.nonRoomDy, w.y2 + base.nonRoomDy);
      } else if (sel.kind === "custom") {
        const c = floor.customs?.[sel.index];
        if (!c) continue;
        for (const [px, py] of c.points || []) {
          minX = Math.min(minX, px + base.nonRoomDx);
          minY = Math.min(minY, py + base.nonRoomDy);
        }
      } else if (sel.kind === "shaft") {
        const s = floor.shafts?.[sel.index];
        if (!s) continue;
        minX = Math.min(minX, s.x + base.nonRoomDx);
        minY = Math.min(minY, s.y + base.nonRoomDy);
      } else if (sel.kind === "stairs") {
        const s = floor.stairs?.[sel.index];
        if (!s) continue;
        minX = Math.min(minX, s.x + base.nonRoomDx);
        minY = Math.min(minY, s.y + base.nonRoomDy);
      }
    }
    return { minX: isFinite(minX) ? minX : 0, minY: isFinite(minY) ? minY : 0 };
  };

  // Directly type a new Δx / Δy for the pending MOVE — recomputed from
  // the gesture base, so it correctly stacks on top of any earlier
  // resize/move rather than overwriting it.
  const handleEditGroupMoveDelta = (dx, dy) => {
    if (!groupPendingChange || groupPendingChange.lastGesture?.type !== "move")
      return;
    const base = groupGestureBaseRef.current || currentGroupBase();
    setGroupPendingChange(applyGestureToBase(base, { type: "move", dx, dy }));
  };

  // Type an absolute target x / y (west/south of the group's bbox) for
  // the pending MOVE — converted to the equivalent Δx/Δy under the hood.
  const handleEditGroupMoveAbsolute = (newX, newY) => {
    if (!groupPendingChange || groupPendingChange.lastGesture?.type !== "move")
      return;
    const base = groupGestureBaseRef.current || currentGroupBase();
    const { minX, minY } = groupSelectionOriginBBox();
    setGroupPendingChange(
      applyGestureToBase(base, {
        type: "move",
        dx: +(newX - minX).toFixed(3),
        dy: +(newY - minY).toFixed(3),
      })
    );
  };

  // Directly type a new Δ for the pending RESIZE (edge stays as dragged).
  const handleEditGroupResizeDelta = (delta) => {
    if (
      !groupPendingChange ||
      groupPendingChange.lastGesture?.type !== "resize"
    )
      return;
    const base = groupGestureBaseRef.current || currentGroupBase();
    const edge = groupPendingChange.lastGesture.edge;
    setGroupPendingChange(
      applyGestureToBase(base, { type: "resize", edge, delta })
    );
  };

  // ── New project ────────────────────────────────────────────────────
  const hasContent = () =>
    (floor.rooms?.length || 0) > 0 ||
    (floor.stairs?.length || 0) > 0 ||
    (floor.shafts?.length || 0) > 0 ||
    (floor.customs?.length || 0) > 0 ||
    (floor.beams?.length || 0) > 0 ||
    (drawing && (drawing.name || drawing.width > 0));

  const handleNewProject = () => {
    if (
      hasContent() &&
      !window.confirm(
        "Discard the current design and start a new empty project?"
      )
    )
      return;
    setFloor({
      name: "MyBuilding",
      rooms: [],
      stairs: [],
      shafts: [],
      customs: [],
      beams: [],
      columns: [],
      slabs: [],
      texts: [],
    });
    setBbox({ width: 10, height: 12, offsetX: 0, offsetY: 0 });
    setSelectedType(null);
    setDrawing(null);
    setFirstCorner(null);
    setOpeningMode(null);
    setOpeningDraft(null);
    setEditIndex(null);
    setEditKind(null);
    // Fresh project → history irrelevant
    setUndoStack([]);
    setRedoStack([]);
  };

  // ── Open project (load JSON from disk) ─────────────────────────────
  const handleOpenProject = async () => {
    if (
      hasContent() &&
      !window.confirm(
        "Discard the current design and load a project from disk?"
      )
    )
      return;
    try {
      const loaded = await openProjectJson();
      if (!loaded) return; // user cancelled the picker
      // Warn if the file has element types we don't yet edit
      const extras = [];
      if (extras.length) {
        alert(
          `Loaded. This project also has ${extras.join(", ")} which will be ` +
            `preserved on save but can't be edited yet.`
        );
      }
      // Adopt the loaded floor. Recompute a reasonable bbox from the rooms
      // AND customs so the canvas frames the whole plan, not the previous 10×12.
      const allX = [],
        allY = [];
      for (const r of loaded.rooms || []) {
        allX.push(r.x || 0, (r.x || 0) + (r.width || 0));
        allY.push(r.y || 0, (r.y || 0) + (r.height || 0));
      }
      for (const c of loaded.customs || []) {
        for (const [px, py] of c.points || []) {
          allX.push(px);
          allY.push(py);
        }
      }
      for (const s of loaded.shafts || []) {
        const der = shaftDerived(s);
        allX.push(s.x || 0, (s.x || 0) + der.blockW);
        allY.push(s.y || 0, (s.y || 0) + der.blockH);
      }
      for (const s of loaded.stairs || []) {
        const der = stairsDerived(s);
        allX.push(s.x || 0, (s.x || 0) + der.blockW);
        allY.push(s.y || 0, (s.y || 0) + der.blockH);
      }
      for (const w of loaded.beams || []) {
        allX.push(w.x1 || 0, w.x2 || 0);
        allY.push(w.y1 || 0, w.y2 || 0);
      }
      // Width/height must be the true SPAN of the content (max - min),
      // not just the max coordinate — a file whose content sits far
      // from the origin (e.g. imported from a real survey/DXF with
      // large absolute coordinates) would otherwise get a bbox.width
      // equal to that huge coordinate itself, shrinking the whole plan
      // down to a few invisible pixels on screen. offsetX/offsetY track
      // where the content actually starts, so the canvas can correctly
      // centre on it instead of always centring on world (0,0).
      const minX = allX.length ? Math.min(...allX) : 0;
      const maxX = allX.length ? Math.max(...allX) : bbox.width;
      const minY = allY.length ? Math.min(...allY) : 0;
      const maxY = allY.length ? Math.max(...allY) : bbox.height;
      const w = allX.length ? maxX - minX : bbox.width;
      const h = allY.length ? maxY - minY : bbox.height;
      setFloor(
        renumberDuplicateWalls({
          ...loaded,
          // Stage 3x.5: defensive — normalise missing arrays so the
          // canvas / list never crash on `.length` of undefined.
          rooms: loaded.rooms || [],
          customs: loaded.customs || [],
          shafts: loaded.shafts || [],
          stairs: loaded.stairs || [],
          beams: loaded.beams || [],
          texts: loaded.texts || [],
        })
      );
      setBbox({
        width: Math.max(1, Math.ceil(w)),
        height: Math.max(1, Math.ceil(h)),
        offsetX: allX.length ? minX : 0,
        offsetY: allY.length ? minY : 0,
      });
      setSelectedType(null);
      setDrawing(null);
      setFirstCorner(null);
      setOpeningMode(null);
      setOpeningDraft(null);
      setEditIndex(null);
      setEditKind(null);
      // Loaded fresh project → clear history
      setUndoStack([]);
      setRedoStack([]);
    } catch (err) {
      alert("Open failed: " + err.message);
    }
  };

  // ── Save ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      // Send only the fields the Python schema knows about — strip the
      // "type" key on rooms if not needed by dxf; Python already accepts it.
      await saveFloorAndDownload(floor);
    } catch (err) {
      alert("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const hasInProgress =
    !!drawing &&
    (drawing.name ||
      drawing.width > 0 ||
      drawing.height > 0 ||
      (drawing.points && drawing.points.length > 0) ||
      (drawing.isShaft && (drawing.x !== 0 || drawing.y !== 0)) ||
      (drawing.isStairs && (drawing.x !== 0 || drawing.y !== 0)) ||
      (drawing.isBeam && drawing.wallPhase !== "p1"));

  return (
    <PageContainer>
      <SEO
        title="AI Building Designer — Floor Plan Generator — SaudiDune"
        description="Generate residential floor plans with AI and export to AutoCAD DXF."
      />
      <LeftPanel>
        <h3 style={{ margin: "0 0 12px 0" }}>Building Designer</h3>

        {/* New / Open toolbar */}
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          <button onClick={handleNewProject} style={toolbarBtnStyle}>
            New project
          </button>
          <button onClick={handleOpenProject} style={toolbarBtnStyle}>
            Open project…
          </button>
        </div>

        {/* Undo / Redo toolbar — Stage 3s */}
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          <button
            onClick={doUndo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            style={{
              ...toolbarBtnStyle,
              opacity: canUndo ? 1 : 0.4,
              cursor: canUndo ? "pointer" : "not-allowed",
            }}
          >
            ↶ Undo{undoStack.length > 0 ? ` (${undoStack.length})` : ""}
          </button>
          <button
            onClick={doRedo}
            disabled={!canRedo}
            title="Redo (Ctrl+Y or Ctrl+Shift+Z)"
            style={{
              ...toolbarBtnStyle,
              opacity: canRedo ? 1 : 0.4,
              cursor: canRedo ? "pointer" : "not-allowed",
            }}
          >
            ↷ Redo{redoStack.length > 0 ? ` (${redoStack.length})` : ""}
          </button>
          <button
            onClick={() => setShiftPanelOpen((o) => !o)}
            disabled={!canShift}
            title="Shift the whole drawing by X/Y (undoable with Ctrl+Z)"
            style={{
              ...toolbarBtnStyle,
              opacity: canShift ? 1 : 0.4,
              cursor: canShift ? "pointer" : "not-allowed",
              background: shiftPanelOpen ? "#eef7f0" : undefined,
            }}
          >
            ⇄ Shift…
          </button>
        </div>

        {/* Stage 8a: precision toggle moved into ElementForm, right
            after the width/height row — no longer shown here. */}

        {/* Shift X/Y inline panel — Stage 3w */}
        {shiftPanelOpen && canShift && (
          <div
            style={{
              padding: 8,
              marginBottom: 12,
              background: "#f5faf7",
              border: "1px solid #bfdac9",
              borderRadius: 4,
              fontSize: 12,
            }}
          >
            <div style={{ marginBottom: 6, color: "#555" }}>
              Shifts every room, custom, shaft, stairs, and wall by the chosen
              X/Y. Negative and decimal values allowed (e.g. -5, 0.1). Ctrl+Z
              undoes it.
            </div>
            <div
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <label style={{ minWidth: 60 }}>Shift X (m):</label>
              <input
                type="number"
                step="0.1"
                value={shiftXInput}
                onChange={(e) => setShiftXInput(e.target.value)}
                placeholder="e.g. 5 or -5 or 0.1"
                style={{ flex: 1, padding: 4, boxSizing: "border-box" }}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <label style={{ minWidth: 60 }}>Shift Y (m):</label>
              <input
                type="number"
                step="0.1"
                value={shiftYInput}
                onChange={(e) => setShiftYInput(e.target.value)}
                placeholder="e.g. 5 or -5 or 0.1"
                style={{ flex: 1, padding: 4, boxSizing: "border-box" }}
              />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button
                onClick={applyShiftFromInputs}
                style={{
                  padding: "4px 10px",
                  fontSize: 12,
                  background: "#2e8b57",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Apply
              </button>
              <button
                onClick={() => {
                  setShiftPanelOpen(false);
                  setShiftXInput("");
                  setShiftYInput("");
                }}
                style={{
                  padding: "4px 10px",
                  fontSize: 12,
                  background: "#fff",
                  color: "#333",
                  border: "1px solid #bbb",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  shiftToOrigin();
                  setShiftPanelOpen(false);
                }}
                title="Auto-shift so the minimum X and Y become 0"
                style={{
                  padding: "4px 10px",
                  fontSize: 12,
                  background: "#fff",
                  color: "#2e8b57",
                  border: "1px solid #bfdac9",
                  borderRadius: 4,
                  cursor: "pointer",
                  marginLeft: "auto",
                }}
              >
                ↖ Shift to origin
              </button>
            </div>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            Project name
          </label>
          <input
            type="text"
            value={floor.name}
            onChange={(e) => setFloor({ ...floor, name: e.target.value })}
            style={{ width: "100%", padding: 6, boxSizing: "border-box" }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
            Bounding box (m)
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="number"
              value={bbox.width}
              onChange={(e) =>
                setBbox({ ...bbox, width: Number(e.target.value) })
              }
              style={{ width: "50%", padding: 6, boxSizing: "border-box" }}
              placeholder="width"
            />
            <input
              type="number"
              value={bbox.height}
              onChange={(e) =>
                setBbox({ ...bbox, height: Number(e.target.value) })
              }
              style={{ width: "50%", padding: 6, boxSizing: "border-box" }}
              placeholder="height"
            />
          </div>
        </div>

        <ElementForm
          selectedType={selectedType}
          drawing={drawing}
          setDrawing={setDrawing}
          clickMode={clickMode}
          setClickMode={setClickMode}
          openingMode={openingMode}
          setOpeningMode={setOpeningMode}
          openingDraft={openingDraft}
          setOpeningDraft={setOpeningDraft}
          floor={floor}
          setFloor={setFloor}
          editIndex={editIndex}
          onEditElement={handleEditElement}
          onCancelEdit={handleCancelEdit}
          onCommitElement={handleCommitElement}
          onDeleteElement={handleDeleteElement}
          inputStep={inputStep}
          setInputStep={setInputStep}
        />

        {/* Always-visible list of committed elements. Lives outside
            ElementForm so it shows even when no type is selected —
            which is the state right after loading a project. */}
        <ElementsList
          floor={floor}
          editIndex={editIndex}
          editKind={editKind}
          onEditElement={handleEditElement}
          onDeleteElement={handleDeleteElement}
          onCancelEdit={handleCancelEdit}
          selection={selection}
          onToggleSelection={toggleSelection}
          onClearSelection={clearSelection}
          onDeleteSelected={deleteSelected}
          showTexts={showTexts}
          onToggleShowTexts={() => setShowTexts((v) => !v)}
          onDeleteText={handleDeleteText}
          onDeleteOrphanTexts={handleDeleteOrphanTexts}
          canConvertSelection={
            detectedRoomBbox !== null || selectedWallsFormRect() !== null
          }
          onConvertSelection={convertSelectionToRoom}
          onConvertToStairs={convertSelectionToStairs}
          onConvertToShaft={convertSelectionToShaft}
          canMergeBeams={canMergeSelectedBeams()}
          onMergeBeams={mergeSelectedBeams}
          carriedInfo={carriedInfo()}
          onSetCarried={setCarriedBeam}
          canDefinePanel={canDefinePanel()}
          onDefinePanel={definePanel}
          mirrorPanelOpen={mirrorPanelOpen}
          onToggleMirrorPanel={() => {
            setPastePanelOpen(false);
            setMirrorPanelOpen((v) => !v);
          }}
          mirrorAxis={mirrorAxis}
          setMirrorAxis={setMirrorAxis}
          mirrorLine={mirrorLine}
          setMirrorLine={setMirrorLine}
          onApplyMirror={applyMirror}
          onCopySelection={handleCopySelection}
          inputStep={inputStep}
          setInputStep={setInputStep}
          clipboard={clipboard}
          pastePanelOpen={pastePanelOpen}
          onTogglePastePanel={() => {
            setMirrorPanelOpen(false);
            setPastePanelOpen((v) => !v);
          }}
          pasteX={pasteX}
          setPasteX={(v) => setPasteX(parseFloat(v) || 0)}
          pasteY={pasteY}
          setPasteY={(v) => setPasteY(parseFloat(v) || 0)}
          onApplyPaste={applyPaste}
          onClearClipboard={() => {
            setClipboard(null);
            setPastePanelOpen(false);
          }}
          groupChangePending={groupPendingChange}
          onSaveGroupChange={handleSaveGroupChange}
          onCancelGroupChange={handleCancelGroupChange}
          groupOriginBBox={
            groupPendingChange ? groupSelectionOriginBBox() : null
          }
          onEditGroupMoveDelta={handleEditGroupMoveDelta}
          onEditGroupMoveAbsolute={handleEditGroupMoveAbsolute}
          onEditGroupResizeDelta={handleEditGroupResizeDelta}
        />

        <SaveButton
          onClick={handleSave}
          disabled={
            saving ||
            (floor.rooms.length === 0 &&
              (floor.customs || []).length === 0 &&
              (floor.shafts || []).length === 0 &&
              (floor.stairs || []).length === 0 &&
              (floor.beams || []).length === 0)
          }
        >
          {saving
            ? "Saving..."
            : floor.rooms.length === 0 &&
                (floor.customs || []).length === 0 &&
                (floor.shafts || []).length === 0 &&
                (floor.stairs || []).length === 0 &&
                (floor.beams || []).length === 0
              ? "Add at least one element to save"
              : "Save (DXF + JSON)"}
        </SaveButton>
      </LeftPanel>

      <MiddlePanel>
        <PlanCanvas
          bbox={bbox}
          floor={
            /* Hide the room/custom/shaft/stairs/wall being edited from
               the committed list so it doesn't render twice (its preview
               draws it dashed). */
            editIndex !== null
              ? editKind === "custom"
                ? {
                    ...floor,
                    customs: (floor.customs || []).filter(
                      (_, i) => i !== editIndex
                    ),
                  }
                : editKind === "shaft"
                  ? {
                      ...floor,
                      shafts: (floor.shafts || []).filter(
                        (_, i) => i !== editIndex
                      ),
                    }
                  : editKind === "stairs"
                    ? {
                        ...floor,
                        stairs: (floor.stairs || []).filter(
                          (_, i) => i !== editIndex
                        ),
                      }
                    : editKind === "beam"
                      ? {
                          ...floor,
                          beams: (floor.beams || []).filter(
                            (_, i) => i !== editIndex
                          ),
                        }
                      : {
                          ...floor,
                          rooms: floor.rooms.filter((_, i) => i !== editIndex),
                        }
              : floor
          }
          drawing={drawing}
          previewOpening={previewOpening}
          onCanvasClick={handleCanvasClick}
          selection={selection}
          showTexts={showTexts}
          hoverInfo={hoverInfo}
          hasActiveTool={
            selectedType !== null || mirrorPanelOpen || pastePanelOpen
          }
          mirrorPreview={
            mirrorPanelOpen ? { axis: mirrorAxis, line: mirrorLine } : null
          }
          pastePreview={
            pastePanelOpen && clipboard
              ? {
                  x: pasteX,
                  y: pasteY,
                  items: clipboard.items,
                  refX: clipboard.refX,
                  refY: clipboard.refY,
                }
              : null
          }
          onRubberBandSelect={handleRubberBandSelect}
          isEditing={editIndex !== null}
          inputStep={inputStep}
          onElementDrag={handleElementDrag}
          groupPendingChange={groupPendingChange}
          onGroupDragMove={handleGroupDragMove}
          onGroupDragCommit={handleGroupDragEnd}
          onGroupDragStart={handleGroupDragStart}
          onGroupResizeMove={handleGroupResizeMove}
          onGroupResizeCommit={handleGroupResizeEnd}
          onGroupResizeStart={handleGroupResizeStart}
          onCanvasMove={(x, y) => setCursor(x == null ? null : { x, y })}
          clickMode={clickMode}
        />
      </MiddlePanel>

      <RightPanel>
        <h3 style={{ margin: "0 0 12px 0" }}>Element Type</h3>
        <TypePicker
          selectedType={selectedType}
          setSelectedType={handleSelectType}
          inProgressType={hasInProgress ? drawing?.type : null}
          onDiscard={handleDiscardInProgress}
        />
      </RightPanel>
    </PageContainer>
  );
}

// Small shared style for the New / Open toolbar buttons at the top.
const toolbarBtnStyle = {
  flex: 1,
  padding: "6px 8px",
  fontSize: 12,
  fontWeight: 500,
  background: "white",
  color: "#333",
  border: "1px solid #ccc",
  borderRadius: 4,
  cursor: "pointer",
};

// Standard ray-casting point-in-polygon test. Returns true when (x,y)
// lies inside the closed polygon defined by `points`.
function pointInPolygon(x, y, points) {
  if (!points || points.length < 3) return false;
  let inside = false;
  const n = points.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = points[i];
    const [xj, yj] = points[j];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
